import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getAuthToken, getUsuarioSistemaId } from '../utils/auth';
import Lightbox from './Lightbox';
import './ChatWidget.css';
import BASE_URL from '../utils/api';
import { CATEGORIAS_EMOJI, ESTILO_BANDEIRA } from '../utils/emojis';

// Emojis: mesma lista e ordem do publicar postagem e do fórum (utils/emojis)

// API helper - robusto com retry e log
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const bases = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
  ];
  
  let lastError = null;
  
  for (const url of bases) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const config = { method, headers };
      if (data && (method === 'POST' || method === 'PUT')) {
        config.body = JSON.stringify(data);
      }
      
      console.log(`[ChatWidget] ${method} ${url}`, data ? JSON.stringify(data).substring(0, 200) : '');
      
      const res = await fetch(url, config);
      
      if (res.ok) {
        const contentType = res.headers.get('Content-Type') || '';
        const text = await res.text();
        console.log(`[ChatWidget] Response OK:`, text.substring(0, 200));
        if (contentType.includes('application/json') && text) {
          try { return JSON.parse(text); } catch { return text; }
        }
        return text || true;
      } else {
        const errorText = await res.text();
        console.error(`[ChatWidget] Response Error ${res.status}:`, errorText);
        lastError = new Error(`${res.status}: ${errorText}`);
      }
    } catch (e) {
      console.error(`[ChatWidget] Fetch error for ${url}:`, e.message);
      lastError = e;
    }
  }
  
  throw lastError || new Error(`Nenhuma URL funcionou para ${method} ${endpoint}`);
};

// API helper for FormData (ChatPessoas uses [FromForm])
const apiCallFormData = async (endpoint, formData) => {
  const bases = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
  ];
  let lastError = null;
  for (const url of bases) {
    try {
      const headers = {};
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      console.log(`[ChatWidget] POST FormData ${url}`);
      const res = await fetch(url, { method: 'POST', headers, body: formData });
      if (res.ok) {
        const text = await res.text();
        const ct = res.headers.get('Content-Type') || '';
        if (ct.includes('application/json') && text) {
          try { return JSON.parse(text); } catch { return text; }
        }
        return text || true;
      } else {
        const errorText = await res.text();
        console.error(`[ChatWidget] FormData Error ${res.status}:`, errorText);
        lastError = new Error(`${res.status}: ${errorText}`);
      }
    } catch (e) {
      console.error(`[ChatWidget] FormData fetch error for ${url}:`, e.message);
      lastError = e;
    }
  }
  throw lastError || new Error(`Nenhuma URL funcionou para POST FormData ${endpoint}`);
};

// Extrai primeira letra válida
const getInitial = (name) => {
  if (!name) return '?';
  const clean = name.replace(/[^a-zA-ZÀ-ÿ]/g, '');
  return clean.charAt(0).toUpperCase() || '?';
};

// Verifica se o texto é um único emoji (sem outros caracteres)
const isSingleEmoji = (text) => {
  if (!text || typeof text !== 'string') return false;
  // Remove espaços em branco
  const trimmed = text.trim();
  if (!trimmed) return false;
  
  // Regex para emoji (suporta emojis com múltiplos code points como 👨‍👩‍👧‍👦)
  const emojiRegex = /^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)$/u;
  
  // Tentar alternativa mais robusta: separar por grapheme clusters
  const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });
  const segments = Array.from(segmenter.segment(trimmed));
  
  // Deve ter exatamente 1 grapheme e esse grapheme deve ser um emoji
  if (segments.length !== 1) return false;
  
  const char = segments[0].segment;
  
  // Verificar se é emoji usando regex unicode
  const emojiPattern = /\p{Emoji}/u;
  return emojiPattern.test(char);
};

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [view, setView] = useState('main'); // 'main' | 'chat'
  const [activeTab, setActiveTab] = useState('grupos'); // 'pessoas' | 'grupos'
  const [searchTerm, setSearchTerm] = useState('');

  const handleClose = () => {
    if (window.innerWidth <= 768) {
      setIsClosing(true);
      setTimeout(() => { setIsOpen(false); setIsClosing(false); }, 300);
    } else {
      setIsOpen(false);
    }
  };

  // Listen for mobile footer open-chat event
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('open-chat-widget', handler);
    return () => window.removeEventListener('open-chat-widget', handler);
  }, []);
  
  // Grupos
  const [grupos, setGrupos] = useState([]);
  const [gruposLoading, setGruposLoading] = useState(false);
  
  // Pessoas
  const [conexoes, setConexoes] = useState([]); // people I follow (UsuarioIdOrigem = me)
  const [conversasPessoas, setConversasPessoas] = useState([]); // people I already have messages with
  const [pessoasSearch, setPessoasSearch] = useState([]); // all users from search
  const [pessoasLoading, setPessoasLoading] = useState(false);
  const [lastMessages, setLastMessages] = useState({}); // map userId -> last message text
  
  // Chat atual
  const [selectedGrupo, setSelectedGrupo] = useState(null);
  const [selectedPessoa, setSelectedPessoa] = useState(null); // { id, nome, login }
  const [chatType, setChatType] = useState('grupo'); // 'grupo' | 'pessoa'
  const [mensagens, setMensagens] = useState([]);
  const [mensagensLoading, setMensagensLoading] = useState(false);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [userNames, setUserNames] = useState({});
  const [userLogins, setUserLogins] = useState({});
  
  // Upload
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  
  // Arquivos pendentes para envio (ainda não enviados)
  const [arquivosPendentes, setArquivosPendentes] = useState([]);
  const [showAllFilesPopup, setShowAllFilesPopup] = useState(false);
  
  // Câmera real
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  // Edição de mensagem
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Confirmação de exclusão
  const [confirmDeleteMsgId, setConfirmDeleteMsgId] = useState(null);
  
  // Dropdown de ações por mensagem
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const actionMenuRef = useRef(null);
  const actionMenuDropdownRef = useRef(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0 });
  
  // Modo seleção de mensagens
  const [selectMode, setSelectMode] = useState(false);
  const [selectedMsgIds, setSelectedMsgIds] = useState(new Set());
  const [confirmDeleteSelected, setConfirmDeleteSelected] = useState(false);
  
  // Lightbox para imagens
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Group info panel
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]); // [{id, nome, login, isAdmin, isOwner}]
  const [groupDescription, setGroupDescription] = useState('');
  const [groupMedia, setGroupMedia] = useState([]); // [{src, type, msgId}]
  const [showMediaGallery, setShowMediaGallery] = useState(false);
  const [groupInfoLoading, setGroupInfoLoading] = useState(false);
  
  // Member profile view
  const [selectedMember, setSelectedMember] = useState(null); // {id, nome, login, isAdmin, isOwner, membershipId}
  const [confirmRemoveMember, setConfirmRemoveMember] = useState(null);
  const [removingMember, setRemovingMember] = useState(false);
  const [connectingMember, setConnectingMember] = useState(false);
  
  // Member selection mode (bulk remove)
  const [memberSelectMode, setMemberSelectMode] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState(new Set());
  const [confirmBulkRemoveMembers, setConfirmBulkRemoveMembers] = useState(false);
  const [removingBulkMembers, setRemovingBulkMembers] = useState(false);

  // Admin management popup
  const [showAdminPopup, setShowAdminPopup] = useState(false);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminSelectedMembershipIds, setAdminSelectedMembershipIds] = useState(new Set());
  const [promoteSelectedMembershipIds, setPromoteSelectedMembershipIds] = useState(new Set());

  // Add members popup
  const [showAddMembersPopup, setShowAddMembersPopup] = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [addMemberResults, setAddMemberResults] = useState([]);
  const [addMemberSelected, setAddMemberSelected] = useState(new Set());
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [addingMembers, setAddingMembers] = useState(false);
  const addMemberSearchTimerRef = useRef(null);

  // Tooltip do remetente (position: fixed, fora do overflow)
  const [senderTooltip, setSenderTooltip] = useState(null); // { name, login, color, top, left }
  
  // Emoji picker
  const emojiRef = useRef(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiCategory, setEmojiCategory] = useState('faces');
  
  // Conexão tracking
  const [conexaoIds, setConexaoIds] = useState(new Set()); // IDs de pessoas conectadas
  const [confirmDisconnect, setConfirmDisconnect] = useState(null); // { id, nome } or null
  const [conexaoLoading, setConexaoLoading] = useState(false);
  
  // Mensagens não lidas
  const [unreadByContact, setUnreadByContact] = useState({}); // { userId: count }
  const [unreadByGroup, setUnreadByGroup] = useState({}); // { grupoId: count }
  const [totalUnread, setTotalUnread] = useState(0);
  
  // Limite de 3 meses
  const [showAllMessages, setShowAllMessages] = useState(false);
  const [confirmLoadOlder, setConfirmLoadOlder] = useState(false);
  const [allMensagens, setAllMensagens] = useState([]); // todas as mensagens sem filtro
  
  // Scroll ref
  const messagesEndRef = useRef(null);
  
  const usuarioLogadoId = getUsuarioSistemaId();

  // Cache de arquivos de ChatPessoas: { [msgId]: objectURL }
  const [pessoaFileUrls, setPessoaFileUrls] = useState({});
  const pessoaFileFetchingRef = useRef(new Set());

  // Buscar arquivo de ChatPessoas por msgId e cachear como objectURL
  const fetchPessoaFile = useCallback(async (msgId) => {
    if (!msgId || pessoaFileFetchingRef.current.has(msgId)) return;
    pessoaFileFetchingRef.current.add(msgId);
    try {
      const token = getAuthToken();
      const bases = [BASE_URL.replace(/\/api\/?$/, '')];
      for (const base of bases) {
        try {
          const res = await fetch(`${base}/api/ChatPessoas/${msgId}/arquivo`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          if (res.ok) {
            const blob = await res.blob();
            if (blob.size > 0) {
              const url = URL.createObjectURL(blob);
              setPessoaFileUrls(prev => ({ ...prev, [msgId]: url }));
              return;
            }
          }
        } catch (e) { continue; }
      }
      setPessoaFileUrls(prev => ({ ...prev, [msgId]: null }));
    } catch (e) {
      setPessoaFileUrls(prev => ({ ...prev, [msgId]: null }));
    } finally {
      pessoaFileFetchingRef.current.delete(msgId);
    }
  }, []);

  // Cache de fotos de perfil: { [usuarioSistemaId]: url_or_null }
  const [userPhotos, setUserPhotos] = useState({});
  const userPhotosFetchingRef = useRef(new Set());
  // Cache de fotos de grupo: { [grupoId]: url_or_null }
  const [groupPhotos, setGroupPhotos] = useState({});

  // Buscar foto de perfil por UsuarioSistemaId
  const fetchUserPhoto = useCallback(async (uid) => {
    if (!uid || userPhotosFetchingRef.current.has(uid)) return;
    userPhotosFetchingRef.current.add(uid);
    try {
      const bases = [BASE_URL.replace(/\/api\/?$/, '')];
      const token = getAuthToken();
      for (const base of bases) {
        try {
          const res = await fetch(`${base}/api/DadosUsuario/foto-por-usuario/${uid}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          if (res.ok) {
            const blob = await res.blob();
            if (blob.size > 0) {
              const url = URL.createObjectURL(blob);
              setUserPhotos(prev => ({ ...prev, [uid]: url }));
              return;
            }
          }
          // 404 = sem foto
          if (res.status === 404) {
            setUserPhotos(prev => ({ ...prev, [uid]: null }));
            return;
          }
        } catch (e) { continue; }
      }
      setUserPhotos(prev => ({ ...prev, [uid]: null }));
    } catch (e) {
      setUserPhotos(prev => ({ ...prev, [uid]: null }));
    } finally {
      userPhotosFetchingRef.current.delete(uid);
    }
  }, []);

  // Extrair foto do grupo do byte[] retornado pelo GET
  const extractGroupPhoto = useCallback((grupo) => {
    const gid = grupo.Id || grupo.id;
    const foto = grupo.FotoGrupo || grupo.fotoGrupo;
    if (!foto) {
      setGroupPhotos(prev => ({ ...prev, [gid]: null }));
      return;
    }
    try {
      if (typeof foto === 'string' && foto.length > 0) {
        const url = foto.startsWith('data:') ? foto : `data:image/jpeg;base64,${foto}`;
        setGroupPhotos(prev => ({ ...prev, [gid]: url }));
      } else if (Array.isArray(foto) && foto.length > 0) {
        const bytes = Uint8Array.from(foto);
        const blob = new Blob([bytes], { type: 'image/jpeg' });
        const url = URL.createObjectURL(blob);
        setGroupPhotos(prev => ({ ...prev, [gid]: url }));
      } else {
        setGroupPhotos(prev => ({ ...prev, [gid]: null }));
      }
    } catch (e) {
      setGroupPhotos(prev => ({ ...prev, [gid]: null }));
    }
  }, []);

  // Helper: renderizar avatar com foto ou inicial
  const renderAvatar = (photoUrl, name, className = 'chat-widget-item-avatar') => {
    if (photoUrl) {
      return (
        <div className={className} style={{ padding: 0, overflow: 'hidden' }}>
          <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
        </div>
      );
    }
    return (
      <div className={className}>
        {getInitial(name)}
      </div>
    );
  };

  // Carregar grupos (dono + membro)
  const carregarGrupos = useCallback(async () => {
    if (!usuarioLogadoId) return;
    setGruposLoading(true);
    try {
      // Grupos que sou dono
      const todosGrupos = await apiCall('Grupo', 'GET');
      const arrGrupos = Array.isArray(todosGrupos) ? todosGrupos : [];
      const meusDonos = arrGrupos.filter(g => {
        const donoId = g.UsuarioSistemaId || g.usuarioSistemaId;
        return donoId === usuarioLogadoId;
      });
      
      // Grupos que faço parte
      const meusGruposIds = new Set(meusDonos.map(g => g.Id || g.id));
      
      // Buscar todos usuarios_grupo do usuário logado
      // Como não tem endpoint específico, vou buscar de cada grupo
      // Alternativa: buscar todos os grupos e filtrar pelo usuarios_grupo
      for (const g of arrGrupos) {
        const gid = g.Id || g.id;
        if (meusGruposIds.has(gid)) continue;
        try {
          const membros = await apiCall(`UsuariosGrupo/grupo/${gid}`, 'GET');
          const arr = Array.isArray(membros) ? membros : [];
          const souMembro = arr.some(m => {
            const uid = m.UsuarioSistemaId || m.usuarioSistemaId;
            return uid === usuarioLogadoId;
          });
          if (souMembro) meusGruposIds.add(gid);
        } catch (e) { /* ignore */ }
      }
      
      const gruposFinais = arrGrupos.filter(g => meusGruposIds.has(g.Id || g.id));
      setGrupos(gruposFinais);
      // Extrair fotos dos grupos
      gruposFinais.forEach(g => extractGroupPhoto(g));
    } catch (err) {
      console.error('Erro ao carregar grupos', err);
      setGrupos([]);
    } finally {
      setGruposLoading(false);
    }
  }, [usuarioLogadoId]);

  useEffect(() => {
    if (isOpen && activeTab === 'grupos') {
      carregarGrupos();
    }
  }, [isOpen, activeTab, carregarGrupos]);

  // Helper: fetch DadosUsuario + UsuarioSistema for a user id
  const fetchUserInfo = async (uid) => {
    let nome = 'Usuário';
    let login = '';
    try {
      const dados = await apiCall(`DadosUsuario/por-usuario/${uid}`, 'GET');
      nome = dados?.NomeUsuario || dados?.nomeUsuario || 'Usuário';
    } catch { /* ignore */ }
    try {
      const usr = await apiCall(`UsuarioSistema/${uid}`, 'GET');
      login = usr?.Login || usr?.login || '';
    } catch { /* ignore */ }
    return { id: uid, nome, login };
  };

  // Carregar conexões (onde eu sou UsuarioIdOrigem)
  const carregarPessoas = useCallback(async () => {
    if (!usuarioLogadoId) return;
    setPessoasLoading(true);
    try {
      const uid = parseInt(usuarioLogadoId, 10);

      // 1) Carregar conexões: ConexaoUsuario GET all, filtrar UsuarioIdOrigem === me
      const allConexoes = await apiCall('ConexaoUsuario', 'GET');
      const arr = Array.isArray(allConexoes) ? allConexoes : [];
      const minhasConexoes = arr.filter(c => {
        const origem = c.UsuarioIdOrigem || c.usuarioIdOrigem;
        return parseInt(origem, 10) === uid;
      });
      // Buscar dados de cada conexão
      const conexoesComDados = await Promise.all(
        minhasConexoes.map(async (c) => {
          const destId = c.UsuarioIdDestino || c.usuarioIdDestino;
          return await fetchUserInfo(destId);
        })
      );
      setConexoes(conexoesComDados);
      setConexaoIds(new Set(conexoesComDados.map(c => parseInt(c.id, 10))));
      // Buscar fotos de perfil das conexões
      conexoesComDados.forEach(c => {
        if (!(c.id in userPhotos)) fetchUserPhoto(c.id);
      });

      // 2) Carregar contatos com conversas existentes
      const contatosIds = await apiCall(`ChatPessoas/contatos/${uid}`, 'GET');
      const arrContatos = Array.isArray(contatosIds) ? contatosIds : [];
      const conversasComDados = await Promise.all(
        arrContatos.map(async (cid) => {
          return await fetchUserInfo(cid);
        })
      );
      setConversasPessoas(conversasComDados);
      // Buscar fotos de perfil dos contatos
      conversasComDados.forEach(c => {
        if (!(c.id in userPhotos)) fetchUserPhoto(c.id);
      });

      // 3) Buscar última mensagem de cada conversa
      const lastMsgs = {};
      for (const pessoa of conversasComDados) {
        try {
          const msgs = await apiCall(`ChatPessoas/conversa/${uid}/${pessoa.id}`, 'GET');
          const arr = Array.isArray(msgs) ? msgs : [];
          if (arr.length > 0) {
            const ultimaMsg = arr[arr.length - 1];
            lastMsgs[pessoa.id] = ultimaMsg.Mensagem || ultimaMsg.mensagem || '';
          }
        } catch (e) {
          console.debug('Erro ao buscar última mensagem', e);
        }
      }
      setLastMessages(lastMsgs);
    } catch (err) {
      console.error('Erro ao carregar pessoas', err);
      setConexoes([]);
      setConversasPessoas([]);
    } finally {
      setPessoasLoading(false);
    }
  }, [usuarioLogadoId]);

  useEffect(() => {
    if (isOpen && activeTab === 'pessoas') {
      carregarPessoas();
    }
  }, [isOpen, activeTab, carregarPessoas]);

  // Listen for external request to open chat with person
  useEffect(() => {
    const handleOpenChatWithPerson = () => {
      const dataStr = localStorage.getItem('openChatWithPerson');
      if (dataStr) {
        try {
          const pessoa = JSON.parse(dataStr);
          localStorage.removeItem('openChatWithPerson');
          setIsOpen(true);
          setActiveTab('pessoas');
          abrirChatPessoa(pessoa);
        } catch (e) {
          console.error('Erro ao abrir chat com pessoa', e);
        }
      }
    };
    window.addEventListener('openChatWithPerson', handleOpenChatWithPerson);
    return () => window.removeEventListener('openChatWithPerson', handleOpenChatWithPerson);
  }, []);

  // Pesquisar todos os usuários do sistema (para aba pessoas com searchTerm)
  const pesquisarUsuarios = useCallback(async (termo) => {
    if (!termo || !termo.trim()) {
      setPessoasSearch([]);
      return;
    }
    try {
      const uid = parseInt(usuarioLogadoId, 10);
      const allUsers = await apiCall('UsuarioSistema', 'GET');
      const arrUsers = Array.isArray(allUsers) ? allUsers : [];
      // Excluir eu mesmo
      const outros = arrUsers.filter(u => {
        const id = u.Id || u.id;
        return parseInt(id, 10) !== uid;
      });
      // Buscar DadosUsuario para ter o nome
      const comDados = await Promise.all(
        outros.map(async (u) => {
          const id = u.Id || u.id;
          const login = u.Login || u.login || '';
          let nome = 'Usuário';
          try {
            const dados = await apiCall(`DadosUsuario/por-usuario/${id}`, 'GET');
            nome = dados?.NomeUsuario || dados?.nomeUsuario || 'Usuário';
          } catch { /* ignore */ }
          return { id, nome, login };
        })
      );
      // Filtrar pelo termo
      const termoLower = termo.toLowerCase();
      const filtrados = comDados.filter(p =>
        p.nome.toLowerCase().includes(termoLower) ||
        p.login.toLowerCase().includes(termoLower)
      );
      setPessoasSearch(filtrados);
      // Buscar fotos de perfil dos resultados
      filtrados.forEach(p => {
        if (!(p.id in userPhotos)) fetchUserPhoto(p.id);
      });
    } catch (err) {
      console.error('Erro ao pesquisar usuários', err);
      setPessoasSearch([]);
    }
  }, [usuarioLogadoId]);

  // Debounce da pesquisa de pessoas
  const searchTimerRef = useRef(null);
  useEffect(() => {
    if (activeTab === 'pessoas' && searchTerm.trim()) {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        pesquisarUsuarios(searchTerm);
      }, 400);
      return () => clearTimeout(searchTimerRef.current);
    } else {
      setPessoasSearch([]);
    }
  }, [searchTerm, activeTab, pesquisarUsuarios]);

  // Ref to track if first load of messages happened
  const mensagensFirstLoadRef = useRef(true);

  // Carregar mensagens do grupo
  const carregarMensagens = useCallback(async () => {
    if (!selectedGrupo) return;
    const isFirstLoad = mensagensFirstLoadRef.current;
    if (isFirstLoad) setMensagensLoading(true);
    try {
      const gid = selectedGrupo.Id || selectedGrupo.id;
      const res = await apiCall(`ChatGrupo/grupo/${gid}`, 'GET');
      const arr = Array.isArray(res) ? res : [];
      
      // Only update state if messages actually changed (compare by length + last id)
      setMensagens(prev => {
        if (prev.length === arr.length) {
          const prevLastId = prev.length > 0 ? (prev[prev.length - 1].Id || prev[prev.length - 1].id) : null;
          const newLastId = arr.length > 0 ? (arr[arr.length - 1].Id || arr[arr.length - 1].id) : null;
          if (prevLastId === newLastId) {
            // Check if any message was edited or deleted
            const changed = arr.some((m, i) => {
              const oldMsg = prev[i];
              if (!oldMsg) return true;
              return (m.Mensagem || m.mensagem) !== (oldMsg.Mensagem || oldMsg.mensagem)
                || (m.MensagemApagada || m.mensagemApagada || 0) !== (oldMsg.MensagemApagada || oldMsg.mensagemApagada || 0);
            });
            if (!changed) return prev;
          }
        }
        return arr;
      });
      
      // Buscar nomes e logins dos usuários
      const userIds = [...new Set(arr.map(m => m.UsuarioSistemaId || m.usuarioSistemaId))];
      for (const uid of userIds) {
        if (!userNames[uid]) {
          try {
            const dados = await apiCall(`DadosUsuario/por-usuario/${uid}`, 'GET');
            const nome = dados?.NomeUsuario || dados?.nomeUsuario || 'Usuário';
            setUserNames(prev => ({ ...prev, [uid]: nome }));
          } catch { /* ignore */ }
        }
        if (!userLogins[uid]) {
          try {
            const usr = await apiCall(`UsuarioSistema/${uid}`, 'GET');
            const login = usr?.Login || usr?.login || '';
            setUserLogins(prev => ({ ...prev, [uid]: login }));
          } catch { /* ignore */ }
        }
      }
      mensagensFirstLoadRef.current = false;
    } catch (err) {
      console.error('Erro ao carregar mensagens', err);
      if (isFirstLoad) setMensagens([]);
    } finally {
      if (isFirstLoad) setMensagensLoading(false);
    }
  }, [selectedGrupo, userNames]);

  // Ref to track if first load of pessoa messages happened
  const mensagensPessoaFirstLoadRef = useRef(true);

  // Carregar informações do grupo (membros, descrição, mídia)
  const carregarInfoGrupo = useCallback(async () => {
    if (!selectedGrupo) return;
    setGroupInfoLoading(true);
    try {
      const gid = selectedGrupo.Id || selectedGrupo.id;
      const ownerId = selectedGrupo.UsuarioSistemaId || selectedGrupo.usuarioSistemaId;
      const descricao = selectedGrupo.Descricao || selectedGrupo.descricao || '';
      setGroupDescription(descricao);

      // Fetch group members
      const membersRes = await apiCall(`UsuariosGrupo/grupo/${gid}`, 'GET');
      const membersArr = Array.isArray(membersRes) ? membersRes : [];
      
      // Build member list with names and photos
      const memberList = await Promise.all(
        membersArr.map(async (member) => {
          const uid = member.UsuarioSistemaId || member.usuarioSistemaId;
          const isAdmin = (member.AdminGrupo || member.adminGrupo || 0) === 1;
          let nome = 'Usuário';
          let login = '';
          try {
            const dados = await apiCall(`DadosUsuario/por-usuario/${uid}`, 'GET');
            nome = dados?.NomeUsuario || dados?.nomeUsuario || 'Usuário';
          } catch { /* ignore */ }
          try {
            const usr = await apiCall(`UsuarioSistema/${uid}`, 'GET');
            login = usr?.Login || usr?.login || '';
          } catch { /* ignore */ }
          if (!(uid in userPhotos)) fetchUserPhoto(uid);
          const membershipId = member.Id || member.id;
          return { id: uid, nome, login, isAdmin, isOwner: parseInt(uid, 10) === parseInt(ownerId, 10), membershipId };
        })
      );

      // Add owner if not in members list
      const ownerInList = memberList.some(m => parseInt(m.id, 10) === parseInt(ownerId, 10));
      if (!ownerInList && ownerId) {
        let ownerNome = 'Usuário';
        let ownerLogin = '';
        try {
          const dados = await apiCall(`DadosUsuario/por-usuario/${ownerId}`, 'GET');
          ownerNome = dados?.NomeUsuario || dados?.nomeUsuario || 'Usuário';
        } catch { /* ignore */ }
        try {
          const usr = await apiCall(`UsuarioSistema/${ownerId}`, 'GET');
          ownerLogin = usr?.Login || usr?.login || '';
        } catch { /* ignore */ }
        if (!(ownerId in userPhotos)) fetchUserPhoto(ownerId);
        memberList.unshift({ id: ownerId, nome: ownerNome, login: ownerLogin, isAdmin: true, isOwner: true, membershipId: null });
      }

      // Sort: owner first, then admins, then others
      memberList.sort((a, b) => {
        if (a.isOwner && !b.isOwner) return -1;
        if (!a.isOwner && b.isOwner) return 1;
        if (a.isAdmin && !b.isAdmin) return -1;
        if (!a.isAdmin && b.isAdmin) return 1;
        return 0;
      });

      setGroupMembers(memberList);

      // Extract media from messages
      const media = [];
      for (const m of mensagens) {
        const arquivo = m.Arquivo || m.arquivo;
        const tipoArquivo = m.TipoArquivo || m.tipoArquivo || '';
        const msgId = m.Id || m.id;
        const mensagemApagada = m.MensagemApagada || m.mensagemApagada || 0;
        if (mensagemApagada === 1) continue;
        if (tipoArquivo && tipoArquivo.startsWith('image/') && arquivo) {
          const src = typeof arquivo === 'string'
            ? `data:${tipoArquivo};base64,${arquivo}`
            : URL.createObjectURL(new Blob([new Uint8Array(arquivo)], { type: tipoArquivo }));
          media.push({ src, type: 'image', msgId });
        }
      }
      setGroupMedia(media);
    } catch (err) {
      console.error('Erro ao carregar info do grupo', err);
    } finally {
      setGroupInfoLoading(false);
    }
  }, [selectedGrupo, mensagens, userPhotos]);

  // Carregar mensagens de pessoa (ChatPessoas) - com limite de 3 meses
  const carregarMensagensPessoa = useCallback(async () => {
    if (!selectedPessoa || !usuarioLogadoId) return;
    const isFirstLoad = mensagensPessoaFirstLoadRef.current;
    if (isFirstLoad) setMensagensLoading(true);
    try {
      const uid = parseInt(usuarioLogadoId, 10);
      const pid = parseInt(selectedPessoa.id, 10);
      const res = await apiCall(`ChatPessoas/conversa/${uid}/${pid}`, 'GET');
      const arr = Array.isArray(res) ? res : [];
      
      // Guardar todas as mensagens
      setAllMensagens(arr);
      
      // Filtrar por 3 meses (se showAllMessages não estiver ativo)
      let finalArr = arr;
      if (!showAllMessages) {
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        finalArr = arr.filter(m => {
          const dt = m.DataHora || m.dataHora;
          if (!dt) return true;
          return new Date(dt) >= threeMonthsAgo;
        });
      }
      
      // Only update if messages changed
      setMensagens(prev => {
        if (prev.length === finalArr.length) {
          const prevLastId = prev.length > 0 ? (prev[prev.length - 1].Id || prev[prev.length - 1].id) : null;
          const newLastId = finalArr.length > 0 ? (finalArr[finalArr.length - 1].Id || finalArr[finalArr.length - 1].id) : null;
          if (prevLastId === newLastId) {
            const changed = finalArr.some((m, i) => {
              const oldMsg = prev[i];
              if (!oldMsg) return true;
              return (m.Mensagem || m.mensagem) !== (oldMsg.Mensagem || oldMsg.mensagem)
                || (m.MensagemApagada || m.mensagemApagada || 0) !== (oldMsg.MensagemApagada || oldMsg.mensagemApagada || 0);
            });
            if (!changed) return prev;
          }
        }
        return finalArr;
      });
      
      // Buscar arquivos das mensagens que têm arquivo
      arr.forEach(m => {
        const temArq = m.TemArquivo || m.temArquivo;
        const mid = m.Id || m.id;
        if (temArq && mid && !(mid in pessoaFileUrls)) {
          fetchPessoaFile(mid);
        }
      });
      mensagensPessoaFirstLoadRef.current = false;
    } catch (err) {
      console.error('Erro ao carregar mensagens pessoa', err);
      if (isFirstLoad) {
        setMensagens([]);
        setAllMensagens([]);
      }
    } finally {
      if (isFirstLoad) setMensagensLoading(false);
    }
  }, [selectedPessoa, usuarioLogadoId, showAllMessages]);

  useEffect(() => {
    if (view === 'chat' && chatType === 'grupo' && selectedGrupo) {
      carregarMensagens();
      carregarInfoGrupo(); // auto-load members for role colors
      const interval = setInterval(carregarMensagens, 5000);
      return () => clearInterval(interval);
    }
  }, [view, chatType, selectedGrupo, carregarMensagens]);

  useEffect(() => {
    if (view === 'chat' && chatType === 'pessoa' && selectedPessoa) {
      carregarMensagensPessoa();
      const interval = setInterval(carregarMensagensPessoa, 5000);
      return () => clearInterval(interval);
    }
  }, [view, chatType, selectedPessoa, carregarMensagensPessoa]);

  // Scroll para baixo quando novas mensagens
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [mensagens]);

  // Polling de mensagens não lidas (a cada 10s, sempre ativo)
  useEffect(() => {
    if (!usuarioLogadoId) return;
    const uid = parseInt(usuarioLogadoId, 10);
    
    const fetchUnread = async () => {
      try {
        let totalPessoas = 0;
        let totalGrupos = 0;
        
        // === ChatPessoas: total + por contato ===
        try {
          const totalPessoasRes = await apiCall(`ChatPessoas/nao-visualizadas/${uid}`, 'GET');
          totalPessoas = typeof totalPessoasRes === 'object' ? (totalPessoasRes.total || totalPessoasRes.Total || 0) : (parseInt(totalPessoasRes, 10) || 0);
        } catch (e) { console.error('[ChatWidget] Erro nao-visualizadas pessoas:', e); }
        
        try {
          const porContato = await apiCall(`ChatPessoas/nao-visualizadas-por-contato/${uid}`, 'GET');
          if (Array.isArray(porContato)) {
            const map = {};
            porContato.forEach(item => {
              const contactId = item.UsuarioId || item.usuarioId;
              const count = item.TotalNaoVisualizadas || item.totalNaoVisualizadas || 0;
              if (contactId && count > 0) map[contactId] = count;
            });
            setUnreadByContact(map);
          }
        } catch (e) { console.error('[ChatWidget] Erro nao-visualizadas-por-contato:', e); }
        
        // === VisualizacaoChatGrupo: total + por grupo ===
        try {
          const totalGruposRes = await apiCall(`VisualizacaoChatGrupo/nao-visualizadas/${uid}`, 'GET');
          totalGrupos = typeof totalGruposRes === 'object' ? (totalGruposRes.total || totalGruposRes.Total || 0) : (parseInt(totalGruposRes, 10) || 0);
        } catch (e) { console.error('[ChatWidget] Erro nao-visualizadas grupos:', e); }
        
        try {
          const porGrupo = await apiCall(`VisualizacaoChatGrupo/nao-visualizadas-por-grupo/${uid}`, 'GET');
          if (Array.isArray(porGrupo)) {
            const map = {};
            porGrupo.forEach(item => {
              const grupoId = item.GrupoId || item.grupoId;
              const count = item.TotalNaoVisualizadas || item.totalNaoVisualizadas || 0;
              if (grupoId && count > 0) map[grupoId] = count;
            });
            setUnreadByGroup(map);
          }
        } catch (e) { console.error('[ChatWidget] Erro nao-visualizadas-por-grupo:', e); }
        
        // Total = soma pessoas + grupos
        setTotalUnread(totalPessoas + totalGrupos);
        // Dispatch unread count for MobileFooter
        window.dispatchEvent(new CustomEvent('chat-unread-update', { detail: totalPessoas + totalGrupos }));
      } catch (err) {
        console.error('[ChatWidget] Erro ao buscar não lidas:', err);
      }
    };
    
    fetchUnread();
    const interval = setInterval(fetchUnread, 10000);
    return () => clearInterval(interval);
  }, [usuarioLogadoId]);

  // Outside click handler for emoji picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking the emoji toggle button itself
      if (emojiButtonRef.current && emojiButtonRef.current.contains(event.target)) {
        return;
      }
      if (emojiRef.current && !emojiRef.current.contains(event.target)) {
        setEmojiOpen(false);
      }
    };
    
    if (emojiOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [emojiOpen]);

  // Emoji handlers
  const emojiButtonRef = useRef(null);
  const toggleEmoji = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setEmojiOpen(prev => !prev);
  };
  
  const onEmojiSelect = (emoji) => {
    setNovaMensagem((m) => (m ? m + emoji : emoji));
  };

  // Enviar mensagem - POST separado para texto e cada arquivo
  const enviarMensagem = async (arquivoBase64DaCamera = null) => {
    // === CHAT PESSOA ===
    if (chatType === 'pessoa') {
      if (!selectedPessoa || !usuarioLogadoId) return;
      const uid = parseInt(usuarioLogadoId, 10);
      const pid = parseInt(selectedPessoa.id, 10);

      // Se veio da câmera
      if (arquivoBase64DaCamera) {
        setEnviando(true);
        try {
          const fd = new FormData();
          fd.append('UsuarioSistemaIdOrigem', uid);
          fd.append('UsuarioSistemaIdDestino', pid);
          fd.append('Mensagem', '[Foto]');
          // Converter base64 para Blob
          const byteChars = atob(arquivoBase64DaCamera);
          const byteArr = new Uint8Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
          const blob = new Blob([byteArr], { type: 'image/jpeg' });
          fd.append('Arquivo', blob, 'foto.jpg');
          await apiCallFormData('ChatPessoas', fd);
          await carregarMensagensPessoa();
        } catch (err) {
          console.error('Erro ao enviar foto pessoa:', err);
          alert('Erro ao enviar foto.');
        } finally {
          setEnviando(false);
        }
        return;
      }

      const temTexto = novaMensagem.trim().length > 0;
      const temArquivos = arquivosPendentes.length > 0;
      if (!temTexto && !temArquivos) return;

      setEnviando(true);
      try {
        // POST para texto
        if (temTexto) {
          const fd = new FormData();
          fd.append('UsuarioSistemaIdOrigem', uid);
          fd.append('UsuarioSistemaIdDestino', pid);
          fd.append('Mensagem', novaMensagem.trim());
          await apiCallFormData('ChatPessoas', fd);
        }
        // POST separado para cada arquivo
        for (const arq of arquivosPendentes) {
          const fd = new FormData();
          fd.append('UsuarioSistemaIdOrigem', uid);
          fd.append('UsuarioSistemaIdDestino', pid);
          fd.append('Mensagem', arq.name);
          const byteChars = atob(arq.base64);
          const byteArr = new Uint8Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
          const blob = new Blob([byteArr], { type: arq.type });
          fd.append('Arquivo', blob, arq.name);
          await apiCallFormData('ChatPessoas', fd);
        }
        setNovaMensagem('');
        setArquivosPendentes([]);
        await carregarMensagensPessoa();
      } catch (err) {
        console.error('Erro ao enviar mensagem pessoa:', err);
        alert('Erro ao enviar mensagem.');
      } finally {
        setEnviando(false);
      }
      return;
    }

    // === CHAT GRUPO (original) ===
    // Se veio da câmera, enviar só a foto
    if (arquivoBase64DaCamera) {
      if (!selectedGrupo || !usuarioLogadoId) return;
      setEnviando(true);
      try {
        const gid = parseInt(selectedGrupo.Id || selectedGrupo.id, 10);
        const uid = parseInt(usuarioLogadoId, 10);
        await apiCall('ChatGrupo', 'POST', {
          GrupoId: gid,
          UsuarioSistemaId: uid,
          Mensagem: '[Foto]',
          ArquivoBase64: arquivoBase64DaCamera,
          TipoArquivo: 'image/jpeg',
          Visualizada: 0
        });
        await carregarMensagens();
      } catch (err) {
        console.error('Erro ao enviar foto:', err);
        alert('Erro ao enviar foto.');
      } finally {
        setEnviando(false);
      }
      return;
    }
    
    // Verificar se tem algo para enviar
    const temTexto = novaMensagem.trim().length > 0;
    const temArquivos = arquivosPendentes.length > 0;
    
    if (!temTexto && !temArquivos) return;
    if (!selectedGrupo || !usuarioLogadoId) return;
    
    setEnviando(true);
    try {
      const gid = parseInt(selectedGrupo.Id || selectedGrupo.id, 10);
      const uid = parseInt(usuarioLogadoId, 10);
      
      if (temTexto) {
        console.log('[ChatWidget] Enviando texto:', novaMensagem.trim());
        await apiCall('ChatGrupo', 'POST', {
          GrupoId: gid,
          UsuarioSistemaId: uid,
          Mensagem: novaMensagem.trim(),
          ArquivoBase64: null,
          Visualizada: 0
        });
      }
      
      for (const arq of arquivosPendentes) {
        console.log('[ChatWidget] Enviando arquivo:', arq.name, 'tipo:', arq.type);
        await apiCall('ChatGrupo', 'POST', {
          GrupoId: gid,
          UsuarioSistemaId: uid,
          Mensagem: arq.name,
          ArquivoBase64: arq.base64,
          TipoArquivo: arq.type,
          Visualizada: 0
        });
      }
      
      setNovaMensagem('');
      setArquivosPendentes([]);
      await carregarMensagens();
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      alert('Erro ao enviar mensagem.');
    } finally {
      setEnviando(false);
    }
  };

  // Handler do Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviarMensagem();
    }
  };

  // Iniciar edição de mensagem
  const iniciarEdicao = (msg) => {
    const id = msg.Id || msg.id;
    const texto = msg.Mensagem || msg.mensagem || '';
    setEditingMsgId(id);
    setEditingText(texto);
  };

  // Cancelar edição
  const cancelarEdicao = () => {
    setEditingMsgId(null);
    setEditingText('');
  };

  // Salvar edição
  const salvarEdicao = async () => {
    if (!editingMsgId || !editingText.trim()) return;
    setSavingEdit(true);
    try {
      if (chatType === 'pessoa') {
        await apiCall(`ChatPessoas/${editingMsgId}`, 'PUT', {
          Mensagem: editingText.trim()
        });
        setEditingMsgId(null);
        setEditingText('');
        mensagensPessoaFirstLoadRef.current = true;
        await carregarMensagensPessoa();
      } else {
        await apiCall(`ChatGrupo/${editingMsgId}`, 'PUT', {
          Mensagem: editingText.trim()
        });
        setEditingMsgId(null);
        setEditingText('');
        mensagensFirstLoadRef.current = true;
        await carregarMensagens();
      }
    } catch (err) {
      console.error('Erro ao editar mensagem', err);
    } finally {
      setSavingEdit(false);
    }
  };

  // Deletar mensagem (soft delete) - com confirmação
  const deletarMensagem = async (msgId) => {
    if (!msgId) return;
    try {
      if (chatType === 'pessoa') {
        await apiCall(`ChatPessoas/${msgId}`, 'DELETE');
        mensagensPessoaFirstLoadRef.current = true;
        await carregarMensagensPessoa();
      } else {
        await apiCall(`ChatGrupo/${msgId}`, 'DELETE');
        mensagensFirstLoadRef.current = true;
        await carregarMensagens();
      }
    } catch (err) {
      console.error('Erro ao deletar mensagem', err);
    }
  };

  // Deletar múltiplas mensagens selecionadas
  const deletarMensagensSelecionadas = async () => {
    if (selectedMsgIds.size === 0) return;
    try {
      for (const msgId of selectedMsgIds) {
        if (chatType === 'pessoa') {
          await apiCall(`ChatPessoas/${msgId}`, 'DELETE');
        } else {
          await apiCall(`ChatGrupo/${msgId}`, 'DELETE');
        }
      }
      setSelectedMsgIds(new Set());
      setSelectMode(false);
      if (chatType === 'pessoa') {
        mensagensPessoaFirstLoadRef.current = true;
        await carregarMensagensPessoa();
      } else {
        mensagensFirstLoadRef.current = true;
        await carregarMensagens();
      }
    } catch (err) {
      console.error('Erro ao deletar mensagens selecionadas', err);
    }
  };

  // Toggle seleção de mensagem
  const toggleSelectMsg = (msgId) => {
    setSelectedMsgIds(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      return next;
    });
  };

  // Close action menu on outside click (o menu fica fora do chat, numa camada por cima: conta os dois)
  useEffect(() => {
    const handleOutsideActionMenu = (e) => {
      const naSeta = actionMenuRef.current && actionMenuRef.current.contains(e.target);
      const noMenu = actionMenuDropdownRef.current && actionMenuDropdownRef.current.contains(e.target);
      if (!naSeta && !noMenu) {
        setOpenActionMenuId(null);
      }
    };
    // O menu é fixo na tela: ao rolar o chat ou mudar o tamanho da janela, fecha para não ficar solto
    const fecharMenu = (e) => {
      if (actionMenuDropdownRef.current && actionMenuDropdownRef.current.contains(e.target)) return;
      setOpenActionMenuId(null);
    };
    if (openActionMenuId !== null) {
      document.addEventListener('mousedown', handleOutsideActionMenu);
      window.addEventListener('scroll', fecharMenu, true);
      window.addEventListener('resize', fecharMenu);
      return () => {
        document.removeEventListener('mousedown', handleOutsideActionMenu);
        window.removeEventListener('scroll', fecharMenu, true);
        window.removeEventListener('resize', fecharMenu);
      };
    }
  }, [openActionMenuId]);

  // Posição do menu de opções na tela, ao lado da setinha (abre para cima se não couber embaixo)
  const abrirMenuAcoes = (e, msgId) => {
    e.stopPropagation();
    if (openActionMenuId === msgId) {
      setOpenActionMenuId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const larguraMenu = 160;
    const alturaMenu = 140;
    const left = Math.min(Math.max(8, rect.right - larguraMenu), window.innerWidth - larguraMenu - 8);
    const top = rect.bottom + alturaMenu + 8 > window.innerHeight
      ? Math.max(8, rect.top - alturaMenu - 4)
      : rect.bottom + 4;
    setActionMenuPos({ top, left });
    setOpenActionMenuId(msgId);
  };

  // Handler Enter na edição
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      salvarEdicao();
    } else if (e.key === 'Escape') {
      cancelarEdicao();
    }
  };

  // Upload de arquivo - adiciona ao array de pendentes em vez de enviar
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Filtrar vídeos
    const arquivosValidos = files.filter(file => {
      if (file.type.startsWith('video/')) {
        alert(`Vídeo "${file.name}" não é permitido.`);
        return false;
      }
      return true;
    });
    
    if (arquivosValidos.length === 0) {
      e.target.value = '';
      return;
    }
    
    console.log(`[ChatWidget] ${arquivosValidos.length} arquivo(s) selecionado(s)`);
    
    try {
      // Processar todos os arquivos em paralelo
      const novosArquivos = await Promise.all(
        arquivosValidos.map(async (file) => {
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result;
              const base64Data = result.includes(',') ? result.split(',')[1] : result;
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          
          const isImage = file.type.startsWith('image/');
          return {
            id: Date.now() + Math.random(),
            name: file.name,
            type: file.type,
            base64: base64,
            isImage: isImage,
            preview: isImage ? `data:${file.type};base64,${base64}` : null
          };
        })
      );
      
      setArquivosPendentes(prev => [...prev, ...novosArquivos]);
    } catch (err) {
      console.error('Erro ao processar arquivos', err);
      alert('Erro ao processar os arquivos.');
    } finally {
      e.target.value = '';
    }
  };

  // Remover arquivo pendente
  const removerArquivoPendente = (id) => {
    setArquivosPendentes(prev => prev.filter(a => a.id !== id));
  };

  // Abrir câmera real
  const abrirCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' }, 
        audio: false 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setShowCamera(true);
      setShowUploadPopup(false);
    } catch (err) {
      console.error('[ChatWidget] Erro ao acessar câmera:', err);
      alert('Não foi possível acessar a câmera. Verifique as permissões do navegador.');
    }
  };

  // Fechar câmera
  const fecharCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  // Tirar foto
  const tirarFoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    fecharCamera();
    
    // Converter para base64
    const base64Data = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
    
    setUploading(true);
    try {
      await enviarMensagem(base64Data);
    } catch (err) {
      console.error('Erro ao enviar foto da câmera', err);
      alert('Erro ao enviar foto.');
    } finally {
      setUploading(false);
    }
  };

  // Abrir chat de grupo
  const abrirChatGrupo = async (grupo) => {
    setSelectedGrupo(grupo);
    setSelectedPessoa(null);
    setChatType('grupo');
    setView('chat');
    setMensagens([]);
    mensagensFirstLoadRef.current = true;
    
    // Marcar todas as mensagens do grupo como visualizadas para o usuário logado
    const uid = parseInt(usuarioLogadoId, 10);
    const gid = grupo.Id || grupo.id;
    try {
      await apiCall(`VisualizacaoChatGrupo/marcar-grupo-visualizado/${gid}/${uid}`, 'PUT');
      // Atualizar contagens locais
      setUnreadByGroup(prev => {
        const next = { ...prev };
        delete next[gid];
        return next;
      });
      setTotalUnread(prev => Math.max(0, prev - (unreadByGroup[gid] || 0)));
    } catch (err) {
      console.error('[ChatWidget] Erro ao marcar grupo como visualizado:', err);
    }
  };

  // Abrir chat de pessoa
  const abrirChatPessoa = async (pessoa) => {
    setSelectedPessoa(pessoa);
    setSelectedGrupo(null);
    setChatType('pessoa');
    setView('chat');
    setMensagens([]);
    setShowAllMessages(false);
    setAllMensagens([]);
    mensagensPessoaFirstLoadRef.current = true;
    
    // Marcar mensagens como visualizadas (origemId = quem enviou = a outra pessoa, destinoId = eu)
    const uid = parseInt(usuarioLogadoId, 10);
    const pid = parseInt(pessoa.id, 10);
    try {
      await apiCall(`ChatPessoas/marcar-conversa-visualizada/${pid}/${uid}`, 'PUT');
      // Atualizar contagens locais
      setUnreadByContact(prev => {
        const next = { ...prev };
        delete next[pid];
        return next;
      });
      setTotalUnread(prev => Math.max(0, prev - (unreadByContact[pid] || 0)));
    } catch (err) {
      console.error('[ChatWidget] Erro ao marcar como visualizada:', err);
    }
  };

  // Criar conexão
  const handleCriarConexao = async (pessoaId) => {
    const uid = parseInt(usuarioLogadoId, 10);
    const pid = parseInt(pessoaId, 10);
    setConexaoLoading(true);
    try {
      await apiCall('ConexaoUsuario', 'POST', {
        UsuarioIdOrigem: uid,
        UsuarioIdDestino: pid
      });
      // Verificar se deu certo
      setConexaoIds(prev => new Set([...prev, pid]));
      // Recarregar conexões
      await carregarPessoas();
    } catch (err) {
      console.error('Erro ao criar conexão:', err);
    } finally {
      setConexaoLoading(false);
    }
  };

  // Desfazer conexão
  const handleDesfazerConexao = async (pessoaId) => {
    const uid = parseInt(usuarioLogadoId, 10);
    const pid = parseInt(pessoaId, 10);
    setConexaoLoading(true);
    try {
      await apiCall(`ConexaoUsuario/${uid}/${pid}`, 'DELETE');
      setConexaoIds(prev => {
        const next = new Set(prev);
        next.delete(pid);
        return next;
      });
      setConfirmDisconnect(null);
      await carregarPessoas();
    } catch (err) {
      console.error('Erro ao desfazer conexão:', err);
    } finally {
      setConexaoLoading(false);
    }
  };

  // Voltar para lista
  const voltarParaLista = () => {
    setView('main');
    setSelectedGrupo(null);
    setSelectedPessoa(null);
    setChatType('grupo');
    setMensagens([]);
    setAllMensagens([]);
    setShowAllMessages(false);
    mensagensFirstLoadRef.current = true;
    mensagensPessoaFirstLoadRef.current = true;
    setSelectMode(false);
    setSelectedMsgIds(new Set());
    setOpenActionMenuId(null);
    setShowGroupInfo(false);
    setShowMediaGallery(false);
    setGroupMembers([]);
    setGroupMedia([]);
    setGroupDescription('');
    setSelectedMember(null);
    setConfirmRemoveMember(null);
    setMemberSelectMode(false);
    setSelectedMemberIds(new Set());
    setConfirmBulkRemoveMembers(false);
    setShowAdminPopup(false);
    setShowAddMembersPopup(false);
    setAddMemberSearch('');
    setAddMemberResults([]);
    setAddMemberSelected(new Set());
  };

  // Toggle admin de um membro individual (no perfil)
  const [togglingAdmin, setTogglingAdmin] = useState(false);
  const toggleAdminMember = async (member) => {
    if (!member || !member.membershipId || member.isOwner) return;
    setTogglingAdmin(true);
    try {
      const newAdmin = member.isAdmin ? 0 : 1;
      await apiCall(`UsuariosGrupo/${member.membershipId}`, 'PUT', { AdminGrupo: newAdmin });
      await carregarInfoGrupo();
      // Atualizar o selectedMember local
      setSelectedMember(prev => prev ? { ...prev, isAdmin: !prev.isAdmin } : prev);
    } catch (err) {
      console.error('Erro ao alterar admin', err);
      alert('Erro ao alterar admin.');
    } finally {
      setTogglingAdmin(false);
    }
  };

  // Remover membro do grupo
  const removerMembroDoGrupo = async (member) => {
    if (!member || !member.membershipId) return;
    setRemovingMember(true);
    try {
      await apiCall(`UsuariosGrupo/${member.membershipId}`, 'DELETE');
      // Recarregar info do grupo
      setSelectedMember(null);
      setConfirmRemoveMember(null);
      await carregarInfoGrupo();
    } catch (err) {
      console.error('Erro ao remover membro do grupo', err);
      alert('Erro ao remover membro do grupo.');
    } finally {
      setRemovingMember(false);
    }
  };

  // Abrir chat com membro a partir do perfil de membro
  const abrirChatComMembro = (member) => {
    const pessoa = { id: member.id, nome: member.nome, login: member.login };
    setSelectedMember(null);
    setShowGroupInfo(false);
    setShowMediaGallery(false);
    abrirChatPessoa(pessoa);
  };

  // Criar conexão com membro
  const criarConexaoComMembro = async (member) => {
    const uid = parseInt(usuarioLogadoId, 10);
    const pid = parseInt(member.id, 10);
    setConnectingMember(true);
    try {
      await apiCall('ConexaoUsuario', 'POST', {
        UsuarioIdOrigem: uid,
        UsuarioIdDestino: pid
      });
      setConexaoIds(prev => new Set([...prev, pid]));
      alert('Conexão criada com sucesso!');
    } catch (err) {
      console.error('Erro ao criar conexão:', err);
      alert('Erro ao criar conexão.');
    } finally {
      setConnectingMember(false);
    }
  };

  // Toggle seleção de membro (bulk)
  const toggleSelectMember = (memberId) => {
    setSelectedMemberIds(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  // Remover membros selecionados em lote
  const removerMembrosSelecionados = async () => {
    if (selectedMemberIds.size === 0) return;
    setRemovingBulkMembers(true);
    try {
      const membersToRemove = groupMembers.filter(m => selectedMemberIds.has(parseInt(m.id, 10)));
      for (const member of membersToRemove) {
        if (member.membershipId) {
          await apiCall(`UsuariosGrupo/${member.membershipId}`, 'DELETE');
        }
      }
      setSelectedMemberIds(new Set());
      setMemberSelectMode(false);
      setConfirmBulkRemoveMembers(false);
      await carregarInfoGrupo();
    } catch (err) {
      console.error('Erro ao remover membros', err);
      alert('Erro ao remover membros.');
    } finally {
      setRemovingBulkMembers(false);
    }
  };

  // Toggle admin de um membro (PUT UsuariosGrupo/{membershipId} com AdminGrupo: 0 ou 1)
  const toggleAdminMembro = async (member) => {
    if (!member.membershipId) return;
    setAdminSaving(true);
    try {
      const newAdmin = member.isAdmin ? 0 : 1;
      await apiCall(`UsuariosGrupo/${member.membershipId}`, 'PUT', { AdminGrupo: newAdmin });
      await carregarInfoGrupo();
    } catch (err) {
      console.error('Erro ao alterar admin', err);
      alert('Erro ao alterar admin.');
    } finally {
      setAdminSaving(false);
    }
  };

  // Toggle selection checkbox in admin popup (for admins list)
  const toggleAdminSelection = (membershipId) => {
    setAdminSelectedMembershipIds(prev => {
      const next = new Set(prev);
      if (next.has(membershipId)) next.delete(membershipId);
      else next.add(membershipId);
      return next;
    });
  };

  // Toggle selection checkbox in admin popup (for non-admins to promote)
  const togglePromoteSelection = (membershipId) => {
    setPromoteSelectedMembershipIds(prev => {
      const next = new Set(prev);
      if (next.has(membershipId)) next.delete(membershipId);
      else next.add(membershipId);
      return next;
    });
  };

  // Batch remove admin role (set AdminGrupo = 0)
  const batchRemoveAdmins = async () => {
    if (adminSelectedMembershipIds.size === 0) return;
    setAdminSaving(true);
    try {
      for (const mid of Array.from(adminSelectedMembershipIds)) {
        await apiCall(`UsuariosGrupo/${mid}`, 'PUT', { AdminGrupo: 0 });
      }
      setAdminSelectedMembershipIds(new Set());
      await carregarInfoGrupo();
    } catch (err) {
      console.error('Erro ao remover admin em lote', err);
      alert('Erro ao remover admin.');
    } finally {
      setAdminSaving(false);
    }
  };

  // Batch make admins (set AdminGrupo = 1)
  const batchMakeAdmins = async () => {
    if (promoteSelectedMembershipIds.size === 0) return;
    setAdminSaving(true);
    try {
      for (const mid of Array.from(promoteSelectedMembershipIds)) {
        await apiCall(`UsuariosGrupo/${mid}`, 'PUT', { AdminGrupo: 1 });
      }
      setPromoteSelectedMembershipIds(new Set());
      await carregarInfoGrupo();
    } catch (err) {
      console.error('Erro ao tornar admin em lote', err);
      alert('Erro ao tornar admin.');
    } finally {
      setAdminSaving(false);
    }
  };

  // Pesquisar usuários para adicionar ao grupo
  const pesquisarParaAdicionarMembros = useCallback(async (termo) => {
    if (!termo || !termo.trim()) {
      setAddMemberResults([]);
      return;
    }
    setAddMemberLoading(true);
    try {
      const uid = parseInt(usuarioLogadoId, 10);
      const allUsers = await apiCall('UsuarioSistema', 'GET');
      const arrUsers = Array.isArray(allUsers) ? allUsers : [];
      // Excluir eu mesmo e membros já existentes
      const existingIds = new Set(groupMembers.map(m => parseInt(m.id, 10)));
      const outros = arrUsers.filter(u => {
        const id = parseInt(u.Id || u.id, 10);
        return id !== uid && !existingIds.has(id);
      });
      const comDados = await Promise.all(
        outros.map(async (u) => {
          const id = u.Id || u.id;
          const login = u.Login || u.login || '';
          let nome = 'Usuário';
          try {
            const dados = await apiCall(`DadosUsuario/por-usuario/${id}`, 'GET');
            nome = dados?.NomeUsuario || dados?.nomeUsuario || 'Usuário';
          } catch { /* ignore */ }
          return { id, nome, login };
        })
      );
      const termoLower = termo.toLowerCase();
      const filtrados = comDados.filter(p =>
        p.nome.toLowerCase().includes(termoLower) ||
        p.login.toLowerCase().includes(termoLower)
      );
      setAddMemberResults(filtrados);
      filtrados.forEach(p => { if (!(p.id in userPhotos)) fetchUserPhoto(p.id); });
    } catch (err) {
      console.error('Erro ao pesquisar usuários', err);
      setAddMemberResults([]);
    } finally {
      setAddMemberLoading(false);
    }
  }, [usuarioLogadoId, groupMembers, userPhotos]);

  // Debounce pesquisa add members
  useEffect(() => {
    if (showAddMembersPopup && addMemberSearch.trim()) {
      if (addMemberSearchTimerRef.current) clearTimeout(addMemberSearchTimerRef.current);
      addMemberSearchTimerRef.current = setTimeout(() => {
        pesquisarParaAdicionarMembros(addMemberSearch);
      }, 400);
      return () => clearTimeout(addMemberSearchTimerRef.current);
    } else {
      setAddMemberResults([]);
    }
  }, [addMemberSearch, showAddMembersPopup, pesquisarParaAdicionarMembros]);

  // Adicionar membros selecionados ao grupo
  const adicionarMembrosAoGrupo = async () => {
    if (addMemberSelected.size === 0 || !selectedGrupo) return;
    setAddingMembers(true);
    try {
      const gid = selectedGrupo.Id || selectedGrupo.id;
      for (const userId of addMemberSelected) {
        await apiCall('UsuariosGrupo', 'POST', { GrupoId: gid, UsuarioSistemaId: userId, AdminGrupo: 0 });
      }
      setAddMemberSelected(new Set());
      setAddMemberSearch('');
      setAddMemberResults([]);
      setShowAddMembersPopup(false);
      await carregarInfoGrupo();
    } catch (err) {
      console.error('Erro ao adicionar membros', err);
      alert('Erro ao adicionar membros.');
    } finally {
      setAddingMembers(false);
    }
  };

  // Filtrar grupos
  const gruposFiltrados = grupos.filter(g => {
    if (!searchTerm) return true;
    const nome = g.NomeGrupo || g.nomeGrupo || g.nome || '';
    return nome.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Nome truncado
  const truncateName = (name, maxLen = 20) => {
    if (!name) return '';
    return name.length > maxLen ? name.substring(0, maxLen) + '...' : name;
  };

  // Formatar data/hora
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) {
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ' ' + 
           d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Ícone fixo do chat - esconde quando popup está aberto */}
      {!isOpen && (
        <div className="chat-widget-icon" onClick={() => setIsOpen(true)}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z" fill="white"/>
            <path d="M6 6h12v1.5H6zM6 9h12v1.5H6zM6 12h12v1.5H6z" fill="white"/>
          </svg>
          {totalUnread > 0 && (
            <span className="chat-icon-unread-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>
          )}
        </div>
      )}

      {/* Container do chat */}
      {isOpen && (
        <div className={`chat-widget-container${isClosing ? ' closing' : ''}`}>
          {view === 'main' ? (
            <>
              {/* Header */}
              <div className="chat-widget-header">
                <button className="chat-widget-back chat-widget-mobile-back" onClick={handleClose}>
                  ←
                </button>
                <span className="chat-widget-title">Chat</span>
                <button className="chat-widget-close" onClick={handleClose}>×</button>
              </div>

              {/* Filtro */}
              <div className="chat-widget-search">
                <input
                  type="text"
                  placeholder="Pesquise pessoa ou grupo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Tabs */}
              <div className="chat-widget-tabs">
                <div
                  className={`chat-widget-tab ${activeTab === 'pessoas' ? 'active' : ''}`}
                  onClick={() => setActiveTab('pessoas')}
                >
                  Pessoas
                </div>
                <div
                  className={`chat-widget-tab ${activeTab === 'grupos' ? 'active' : ''}`}
                  onClick={() => setActiveTab('grupos')}
                >
                  Grupos
                </div>
              </div>

              {/* Conteúdo */}
              <div className="chat-widget-content">
                {activeTab === 'pessoas' && (
                  <div className="chat-widget-list">
                    {pessoasLoading && <div className="chat-widget-loading">Carregando pessoas...</div>}
                    
                    {!pessoasLoading && searchTerm.trim() ? (
                      /* Pesquisa ativa: mostrar todos os usuários filtrados */
                      <>
                        {pessoasSearch.length === 0 && (
                          <div className="chat-widget-empty">Nenhuma pessoa encontrada</div>
                        )}
                        {pessoasSearch.map(p => (
                          <div key={p.id} className="chat-widget-pessoa-card" onClick={() => abrirChatPessoa(p)}>
                            <div className="chat-widget-pessoa-top">
                              {renderAvatar(userPhotos[p.id], p.nome)}
                              <div className="chat-widget-pessoa-info">
                                <span className="chat-widget-item-name">{p.nome}</span>
                                {p.login && <span className="chat-widget-item-login">{p.login}</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : !pessoasLoading && (
                      /* Sem pesquisa: mostrar conversas + conexões */
                      <>
                        {/* Conversas recentes */}
                        {conversasPessoas.length > 0 && (
                          <>
                            <div className="chat-widget-section-label">Conversas</div>
                            {conversasPessoas.map(p => (
                              <div key={`conv-${p.id}`} className="chat-widget-pessoa-card" onClick={() => abrirChatPessoa(p)}>
                                <div className="chat-widget-pessoa-top">
                                  {renderAvatar(userPhotos[p.id], p.nome)}
                                  <div className="chat-widget-pessoa-info">
                                    <span className="chat-widget-item-name">{p.nome}</span>
                                    {p.login && <span className="chat-widget-item-login">{p.login}</span>}
                                    {lastMessages[p.id] && (
                                      <span className="chat-widget-last-message">{lastMessages[p.id]}</span>
                                    )}
                                  </div>
                                  {unreadByContact[p.id] > 0 && (
                                    <span className="chat-unread-badge">{unreadByContact[p.id]}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                        
                        {/* Conexões */}
                        {conexoes.length > 0 && (
                          <>
                            <div className="chat-widget-section-label">Conexões</div>
                            {conexoes.map(p => (
                              <div key={`con-${p.id}`} className="chat-widget-pessoa-card" onClick={() => abrirChatPessoa(p)}>
                                <div className="chat-widget-pessoa-top">
                                  {renderAvatar(userPhotos[p.id], p.nome)}
                                  <div className="chat-widget-pessoa-info">
                                    <span className="chat-widget-item-name">{p.nome}</span>
                                    {p.login && <span className="chat-widget-item-login">{p.login}</span>}
                                  </div>
                                  {unreadByContact[p.id] > 0 && (
                                    <span className="chat-unread-badge">{unreadByContact[p.id]}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                        
                        {conversasPessoas.length === 0 && conexoes.length === 0 && (
                          <div className="chat-widget-empty">Nenhuma conexão encontrada</div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {activeTab === 'grupos' && (
                  <div className="chat-widget-list">
                    {gruposLoading && <div className="chat-widget-loading">Carregando grupos...</div>}
                    {!gruposLoading && gruposFiltrados.length === 0 && (
                      <div className="chat-widget-empty">Nenhum grupo encontrado</div>
                    )}
                    {!gruposLoading && gruposFiltrados.map(g => {
                      const nome = g.NomeGrupo || g.nomeGrupo || g.nome || 'Grupo';
                      const gid = g.Id || g.id;
                      return (
                        <div
                          key={gid}
                          className="chat-widget-item"
                          onClick={() => abrirChatGrupo(g)}
                        >
                          {renderAvatar(groupPhotos[gid], nome)}
                          <span className="chat-widget-item-name">{nome}</span>
                          {unreadByGroup[gid] > 0 && (
                            <span className="chat-unread-badge">{unreadByGroup[gid]}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Header do chat */}
              <div className="chat-widget-header">
                <button className="chat-widget-back" onClick={voltarParaLista}>
                  ←
                </button>
                <span className="chat-widget-title">
                  {chatType === 'pessoa' 
                    ? truncateName(selectedPessoa?.nome || 'Pessoa', 18) 
                    : truncateName(selectedGrupo?.NomeGrupo || selectedGrupo?.nomeGrupo || 'Grupo', 18)
                  }
                </span>
                <button className="chat-widget-close" onClick={handleClose}>×</button>
              </div>

              {/* Card de info do grupo - clicável */}
              {chatType === 'grupo' && selectedGrupo && !showGroupInfo && !showMediaGallery && (
                <div className="chat-group-info-card" onClick={() => { carregarInfoGrupo(); setShowGroupInfo(true); }}>
                  <div className="chat-group-info-card-photo">
                    {(() => {
                      const gid = selectedGrupo.Id || selectedGrupo.id;
                      const nome = selectedGrupo.NomeGrupo || selectedGrupo.nomeGrupo || 'Grupo';
                      const photo = groupPhotos[gid];
                      if (photo) {
                        return <img src={photo} alt={nome} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />;
                      }
                      return <span>{getInitial(nome)}</span>;
                    })()}
                  </div>
                  <div className="chat-group-info-card-text">
                    <div className="chat-group-info-card-name">
                      {selectedGrupo.NomeGrupo || selectedGrupo.nomeGrupo || 'Grupo'}
                    </div>
                    <div className="chat-group-info-card-count">
                      {groupMembers.length > 0 ? `${groupMembers.length} membro${groupMembers.length !== 1 ? 's' : ''}` : 'Ver informações do grupo'}
                    </div>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, opacity: 0.5 }}><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" fill="white"/></svg>
                </div>
              )}

              {/* Painel de info do grupo */}
              {chatType === 'grupo' && showGroupInfo && !showMediaGallery && !selectedMember && (
                <div className="chat-group-info-panel">
                  <div className="chat-group-info-panel-scroll">
                    {/* Foto grande do grupo */}
                    <div className="chat-group-info-photo-large" onClick={() => {
                      const gid = selectedGrupo.Id || selectedGrupo.id;
                      const photo = groupPhotos[gid];
                      if (photo) {
                        setLightboxImages([{ url: photo }]);
                        setLightboxIndex(0);
                        setLightboxOpen(true);
                      }
                    }} style={{ cursor: groupPhotos[selectedGrupo.Id || selectedGrupo.id] ? 'pointer' : 'default' }}>
                      {(() => {
                        const gid = selectedGrupo.Id || selectedGrupo.id;
                        const nome = selectedGrupo.NomeGrupo || selectedGrupo.nomeGrupo || 'Grupo';
                        const photo = groupPhotos[gid];
                        if (photo) {
                          return <img src={photo} alt={nome} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />;
                        }
                        return <span>{getInitial(nome)}</span>;
                      })()}
                    </div>
                    {/* Nome do grupo */}
                    <div className="chat-group-info-name">
                      {selectedGrupo.NomeGrupo || selectedGrupo.nomeGrupo || 'Grupo'}
                    </div>
                    {/* Contagem de membros */}
                    <div className="chat-group-info-member-count">
                      {groupMembers.length} membro{groupMembers.length !== 1 ? 's' : ''}
                    </div>

                    {/* Botão adicionar membros - só para admin/owner */}
                    {(() => {
                      const ownerId = selectedGrupo.UsuarioSistemaId || selectedGrupo.usuarioSistemaId;
                      const uid = parseInt(usuarioLogadoId, 10);
                      const isOwner = parseInt(ownerId, 10) === uid;
                      const isAdmin = groupMembers.some(m => parseInt(m.id, 10) === uid && m.isAdmin);
                      if (isOwner || isAdmin) {
                        return (
                          <button className="chat-group-info-add-btn" onClick={() => { setShowAddMembersPopup(true); setAddMemberSearch(''); setAddMemberResults([]); setAddMemberSelected(new Set()); }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="rgb(235, 133, 0)"/></svg>
                            <span>Adicionar membros</span>
                          </button>
                        );
                      }
                      return null;
                    })()}

                    {/* Descrição do grupo */}
                    {groupDescription && (
                      <div className="chat-group-info-section">
                        <div className="chat-group-info-section-title">Descrição</div>
                        <div className="chat-group-info-description">{groupDescription}</div>
                      </div>
                    )}

                    {/* Mídia */}
                    <div className="chat-group-info-section">
                      <div className="chat-group-info-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Mídia</span>
                        {groupMedia.length > 3 && (
                          <span className="chat-group-info-see-all" onClick={() => setShowMediaGallery(true)}>
                            Ver tudo ({groupMedia.length}) →
                          </span>
                        )}
                      </div>
                      {groupMedia.length === 0 ? (
                        <div className="chat-group-info-empty">Nenhuma mídia compartilhada</div>
                      ) : (
                        <div className="chat-group-info-media-grid">
                          {groupMedia.slice(0, 3).map((media, i) => (
                            <img
                              key={i}
                              src={media.src}
                              alt="mídia"
                              className="chat-group-info-media-thumb"
                              onClick={() => {
                                setLightboxImages(groupMedia.map(m => ({ url: m.src })));
                                setLightboxIndex(i);
                                setLightboxOpen(true);
                              }}
                            />
                          ))}
                          {groupMedia.length > 3 && (
                            <div className="chat-group-info-media-more" onClick={() => setShowMediaGallery(true)}>
                              +{groupMedia.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Membros */}
                    <div className="chat-group-info-section">
                      <div className="chat-group-info-section-title">
                        Membros ({groupMembers.length})
                      </div>
                      {groupInfoLoading ? (
                        <div className="chat-group-info-empty">Carregando...</div>
                      ) : (
                        <>
                        {/* Checkbox Selecionar + Gerenciar Admin - só admin/owner */}
                        {(() => {
                          const ownerId = selectedGrupo?.UsuarioSistemaId || selectedGrupo?.usuarioSistemaId;
                          const uid = parseInt(usuarioLogadoId, 10);
                          const isOwner = parseInt(ownerId, 10) === uid;
                          const isAdmin = groupMembers.some(m => parseInt(m.id, 10) === uid && m.isAdmin);
                          if (!isOwner && !isAdmin) return null;
                          return (
                            <div className="chat-member-controls-row">
                              <div className="chat-member-select-toggle" onClick={() => {
                                if (memberSelectMode) {
                                  setMemberSelectMode(false);
                                  setSelectedMemberIds(new Set());
                                } else {
                                  setMemberSelectMode(true);
                                  setSelectedMemberIds(new Set());
                                }
                              }}>
                                <div className={`chat-select-checkbox ${memberSelectMode ? 'checked' : ''}`}>
                                  {memberSelectMode && <svg width="14" height="14" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white"/></svg>}
                                </div>
                                <span style={{ color: 'white', fontSize: 13, fontWeight: 500 }}>
                                  Selecionar{selectedMemberIds.size > 0 ? ` (${selectedMemberIds.size})` : ''}
                                </span>
                              </div>
                              {isOwner && (
                                <button className="chat-admin-manage-btn" onClick={() => setShowAdminPopup(true)}>
                                  Gerenciar admin
                                </button>
                              )}
                            </div>
                          );
                        })()}
                        <div className="chat-group-info-members-list">
                          {groupMembers.map((member) => {
                            const isSelf = parseInt(member.id, 10) === parseInt(usuarioLogadoId, 10);
                            const memberId = parseInt(member.id, 10);
                            const isSelected = selectedMemberIds.has(memberId);
                            const showCheckbox = memberSelectMode;
                            return (
                            <div key={member.id} className={`chat-group-info-member ${!isSelf && !memberSelectMode ? 'clickable' : ''}`} onClick={() => {
                              if (memberSelectMode) {
                                toggleSelectMember(memberId);
                              } else if (!isSelf) {
                                setSelectedMember(member);
                              }
                            }}>
                              {showCheckbox && (
                                <div className={`chat-select-checkbox ${isSelected ? 'checked' : ''}`} style={{ marginRight: 4 }}>
                                  {isSelected && <svg width="14" height="14" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white"/></svg>}
                                </div>
                              )}
                              <div className="chat-group-info-member-avatar">
                                {userPhotos[member.id] ? (
                                  <img src={userPhotos[member.id]} alt={member.nome} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                ) : (
                                  <span>{getInitial(member.nome)}</span>
                                )}
                              </div>
                              <div className="chat-group-info-member-info">
                                <div className="chat-group-info-member-name">
                                  {member.nome}
                                  {isSelf && <span className="chat-group-info-badge" style={{ background: '#666' }}>Você</span>}
                                  {member.isOwner && <span className="chat-group-info-badge owner">Dono</span>}
                                  {member.isAdmin && !member.isOwner && <span className="chat-group-info-badge admin">Admin</span>}
                                </div>
                                {member.login && <div className="chat-group-info-member-login">{member.login}</div>}
                              </div>
                              {!isSelf && !memberSelectMode && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, opacity: 0.4 }}><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" fill="white"/></svg>}
                            </div>
                            );
                          })}
                        </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Botão fechar painel info */}
                  <div className="chat-group-info-close-bar" style={{ display: 'flex', gap: 8 }}>
                    <button className="chat-group-info-close-btn" onClick={() => { setShowGroupInfo(false); setMemberSelectMode(false); setSelectedMemberIds(new Set()); }} style={{ flex: 1 }}>
                      ← Voltar ao chat
                    </button>
                    {memberSelectMode && selectedMemberIds.size > 0 && (
                      <button className="chat-group-info-close-btn" style={{ flex: 1, background: '#f44336' }} onClick={() => setConfirmBulkRemoveMembers(true)}>
                        Remover ({selectedMemberIds.size})
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Perfil do membro */}
              {chatType === 'grupo' && selectedMember && (
                <div className="chat-group-info-panel">
                  <div className="chat-group-info-panel-scroll" style={{ alignItems: 'center', justifyContent: 'center' }}>
                    {/* Foto do membro */}
                    <div className="chat-member-profile-photo">
                      {userPhotos[selectedMember.id] ? (
                        <img src={userPhotos[selectedMember.id]} alt={selectedMember.nome} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        <span>{getInitial(selectedMember.nome)}</span>
                      )}
                    </div>
                    {/* Nome */}
                    <div className="chat-member-profile-name">{selectedMember.nome}</div>
                    {/* Login */}
                    {selectedMember.login && <div className="chat-member-profile-login">{selectedMember.login}</div>}

                    {/* 3 botões de ação */}
                    <div className="chat-member-profile-actions">
                      {/* Mensagem */}
                      <div className="chat-member-profile-action-card" onClick={() => abrirChatComMembro(selectedMember)}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z" fill="rgb(235, 133, 0)"/>
                          <path d="M6 6h12v1.5H6zM6 9h12v1.5H6zM6 12h8v1.5H6z" fill="rgb(235, 133, 0)"/>
                        </svg>
                        <span>Mensagem</span>
                      </div>
                      {/* Conectar */}
                      <div className={`chat-member-profile-action-card ${connectingMember ? 'disabled' : ''}`} onClick={() => { if (!connectingMember) criarConexaoComMembro(selectedMember); }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="rgb(235, 133, 0)"/>
                        </svg>
                        <span>{connectingMember ? '...' : 'Conectar'}</span>
                      </div>
                      {/* Remover - só para admin/owner */}
                      {(() => {
                        const ownerId = selectedGrupo?.UsuarioSistemaId || selectedGrupo?.usuarioSistemaId;
                        const uid = parseInt(usuarioLogadoId, 10);
                        const isOwner = parseInt(ownerId, 10) === uid;
                        const isAdmin = groupMembers.some(m => parseInt(m.id, 10) === uid && m.isAdmin);
                        if ((isOwner || isAdmin) && !selectedMember.isOwner) {
                          return (
                            <div className="chat-member-profile-action-card remove" onClick={() => setConfirmRemoveMember(selectedMember)}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="#f44336"/>
                              </svg>
                              <span>Remover</span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>

                    {/* Botão Tornar/Remover Admin - só owner pode e não no próprio owner */}
                    {(() => {
                      const ownerId = selectedGrupo?.UsuarioSistemaId || selectedGrupo?.usuarioSistemaId;
                      const isOwner = parseInt(ownerId, 10) === parseInt(usuarioLogadoId, 10);
                      if (isOwner && !selectedMember.isOwner && selectedMember.membershipId) {
                        return selectedMember.isAdmin ? (
                          <button
                            className="chat-member-admin-toggle-btn remove-admin"
                            onClick={() => toggleAdminMember(selectedMember)}
                            disabled={togglingAdmin}
                          >
                            {togglingAdmin ? '...' : 'Remover Admin'}
                          </button>
                        ) : (
                          <button
                            className="chat-member-admin-toggle-btn make-admin"
                            onClick={() => toggleAdminMember(selectedMember)}
                            disabled={togglingAdmin}
                          >
                            {togglingAdmin ? '...' : 'Tornar Admin'}
                          </button>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  {/* Botão de voltar */}
                  <div className="chat-group-info-close-bar">
                    <button className="chat-group-info-close-btn" onClick={() => setSelectedMember(null)}>
                      ← Voltar
                    </button>
                  </div>
                </div>
              )}

              {/* Galeria de mídia */}
              {chatType === 'grupo' && showMediaGallery && (
                <div className="chat-group-info-panel">
                  <div className="chat-group-info-panel-scroll">
                    <div className="chat-group-info-section" style={{ paddingTop: 10 }}>
                      <div className="chat-group-info-section-title">
                        Todas as mídias ({groupMedia.length})
                      </div>
                      {groupMedia.length === 0 ? (
                        <div className="chat-group-info-empty">Nenhuma mídia compartilhada</div>
                      ) : (
                        <div className="chat-group-info-media-gallery">
                          {groupMedia.map((media, i) => (
                            <img
                              key={i}
                              src={media.src}
                              alt="mídia"
                              className="chat-group-info-media-gallery-thumb"
                              onClick={() => {
                                setLightboxImages(groupMedia.map(m => ({ url: m.src })));
                                setLightboxIndex(i);
                                setLightboxOpen(true);
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="chat-group-info-close-bar">
                    <button className="chat-group-info-close-btn" onClick={() => setShowMediaGallery(false)}>
                      ← Voltar
                    </button>
                  </div>
                </div>
              )}

              {/* Barra de seleção */}
              {!showGroupInfo && !showMediaGallery && selectMode && (
                <div className="chat-select-bar">
                  <div className="chat-select-bar-left">
                    <div className="chat-select-checkbox checked" style={{ width: 20, height: 20 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white"/></svg>
                    </div>
                    <span>{selectedMsgIds.size} mensagen{selectedMsgIds.size !== 1 ? 's' : ''} selecionada{selectedMsgIds.size !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="chat-select-bar-actions">
                    <button className="chat-select-bar-delete" onClick={() => { if (selectedMsgIds.size > 0) setConfirmDeleteSelected(true); }} disabled={selectedMsgIds.size === 0}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="white"/></svg>
                      Apagar
                    </button>
                    <button className="chat-select-bar-cancel" onClick={() => { setSelectMode(false); setSelectedMsgIds(new Set()); }}>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {/* Mensagens */}
              {!showGroupInfo && !showMediaGallery && (
              <div className="chat-widget-messages">
                {/* Link para ver mensagens anteriores (mais de 3 meses) */}
                {chatType === 'pessoa' && !showAllMessages && allMensagens.length > mensagens.length && (
                  <div className="chat-older-messages-link" onClick={() => setConfirmLoadOlder(true)}>
                    📜 Ver mensagens anteriores
                  </div>
                )}
                {mensagensLoading && <div className="chat-widget-loading">Carregando...</div>}
                {!mensagensLoading && mensagens.length === 0 && (
                  <div className="chat-widget-empty">Nenhuma mensagem ainda</div>
                )}
                {mensagens.map((m, idx) => {
                  // Support both group (UsuarioSistemaId) and person (UsuarioSistemaIdOrigem) messages
                  const uid = m.UsuarioSistemaIdOrigem || m.usuarioSistemaIdOrigem || m.UsuarioSistemaId || m.usuarioSistemaId;
                  const isMe = parseInt(uid, 10) === parseInt(usuarioLogadoId, 10);
                  const fullUserName = chatType === 'pessoa'
                    ? (isMe ? 'Você' : (selectedPessoa?.nome || 'Pessoa'))
                    : (userNames[uid] || 'Usuário');
                  const userName = fullUserName.length > 25 ? fullUserName.substring(0, 25) + '...' : fullUserName;
                  
                  // Determine sender role for group tooltip color and fetch login
                  let senderRoleColor = 'rgb(235, 133, 0)'; // default: rgb(235, 133, 0) (regular)
                  let senderLogin = '';
                  if (chatType === 'grupo' && !isMe) {
                    const ownerId = selectedGrupo?.UsuarioSistemaId || selectedGrupo?.usuarioSistemaId;
                    if (parseInt(uid, 10) === parseInt(ownerId, 10)) {
                      senderRoleColor = '#f44336'; // red for owner
                      const ownerMember = groupMembers.find(gm => parseInt(gm.id, 10) === parseInt(uid, 10));
                      if (ownerMember) senderLogin = ownerMember.login || '';
                    } else {
                      const memberInfo = groupMembers.find(gm => parseInt(gm.id, 10) === parseInt(uid, 10));
                      if (memberInfo) {
                        if (memberInfo.isAdmin) senderRoleColor = '#4caf50'; // green for admin
                        senderLogin = memberInfo.login || '';
                      }
                    }
                  } else if (chatType === 'pessoa' && !isMe) {
                    senderLogin = selectedPessoa?.login || '';
                  }
                  // fallback to cached userLogins if not found from groupMembers/selectedPessoa
                  if (!senderLogin && userLogins[uid]) senderLogin = userLogins[uid];
                  const texto = m.Mensagem || m.mensagem || '';
                  const arquivo = m.Arquivo || m.arquivo;
                  const tipoArquivo = m.TipoArquivo || m.tipoArquivo || '';
                  const msgId = m.Id || m.id;
                  const isEditing = editingMsgId === msgId;
                  const mensagemApagada = m.MensagemApagada || m.mensagemApagada || 0;
                  
                  // ChatPessoas usa TemArquivo flag (arquivo vem via endpoint)
                  const temArquivoPessoa = m.TemArquivo || m.temArquivo;
                  
                  // Verificar se TEM arquivo (grupo: inline base64, pessoa: TemArquivo flag)
                  const temArquivo = temArquivoPessoa || (arquivo && arquivo !== null && (
                    (typeof arquivo === 'string' && arquivo.length > 0) ||
                    (Array.isArray(arquivo) && arquivo.length > 0)
                  ));
                  
                  // Usar TipoArquivo do backend para determinar tipo
                  const isImageFile = temArquivo && tipoArquivo && tipoArquivo.startsWith('image/');
                  const isPdfFile = temArquivo && tipoArquivo && tipoArquivo === 'application/pdf';
                  const isDocFile = temArquivo && tipoArquivo && (tipoArquivo.includes('word') || tipoArquivo === 'text/plain' || tipoArquivo.includes('document'));
                  const isFileMessage = temArquivo && (isPdfFile || isDocFile || isImageFile);
                  
                  // === Mensagem deletada ===
                  if (mensagemApagada === 1) {
                    let deletedText = 'Mensagem deletada';
                    let deletedBg = 'rgb(235, 133, 0)';
                    if (isImageFile) {
                      deletedText = 'Imagem deletada';
                      deletedBg = '#555';
                    } else if (isPdfFile || isDocFile) {
                      deletedText = 'Arquivo deletado';
                      deletedBg = '#555';
                    }
                    return (
                      <div key={msgId || idx} className={`chat-message ${isMe ? 'mine' : 'other'}`}>
                        {!isMe && (
                          <div className="chat-message-sender"
                            onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setSenderTooltip({ name: fullUserName, login: senderLogin, color: senderRoleColor, top: rect.top - 4, left: rect.left });
                            }}
                            onMouseLeave={() => setSenderTooltip(null)}
                          >
                            <span className="chat-sender-name-text" style={{ color: senderRoleColor }}>{userName}</span>
                          </div>
                        )}
                        <div className="chat-message-row">
                          {selectMode && isMe && (
                            <div 
                              className={`chat-select-checkbox ${selectedMsgIds.has(msgId) ? 'checked' : ''}`}
                              onClick={() => toggleSelectMsg(msgId)}
                            >
                              {selectedMsgIds.has(msgId) && (
                                <svg width="14" height="14" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white"/></svg>
                              )}
                            </div>
                          )}
                          <div className="chat-message-bubble-deleted" style={{ backgroundColor: deletedBg }}>
                            <span style={{ fontStyle: 'italic', fontSize: 13, color: 'white' }}>{deletedText}</span>
                            <div style={{ fontSize: 12, color: 'white', marginTop: 4, textAlign: 'right' }}>{formatDateTime(m.DataHora || m.dataHora)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <div key={msgId || idx} className={`chat-message ${isMe ? 'mine' : 'other'}`}>
                      {/* Nome do remetente - sempre aparece para quem não sou eu */}
                      {!isMe && (
                        <div className="chat-message-sender"
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setSenderTooltip({ name: fullUserName, login: senderLogin, color: senderRoleColor, top: rect.top - 4, left: rect.left });
                          }}
                          onMouseLeave={() => setSenderTooltip(null)}
                        >
                          <span className="chat-sender-name-text" style={{ color: senderRoleColor }}>{userName}</span>
                        </div>
                      )}
                      <div className="chat-message-row">
                        {/* Checkbox no modo seleção */}
                        {selectMode && isMe && (
                          <div 
                            className={`chat-select-checkbox ${selectedMsgIds.has(msgId) ? 'checked' : ''}`}
                            onClick={() => toggleSelectMsg(msgId)}
                          >
                            {selectedMsgIds.has(msgId) && (
                              <svg width="14" height="14" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white"/></svg>
                            )}
                          </div>
                        )}
                        {/* Seta para baixo (dropdown) - aparece nas minhas mensagens quando NÃO está em modo seleção */}
                        {isMe && !isEditing && !selectMode && (
                          <div className="chat-msg-action-wrapper" ref={openActionMenuId === msgId ? actionMenuRef : null}>
                            <button 
                              className="chat-message-arrow-btn"
                              onClick={(e) => abrirMenuAcoes(e, msgId)}
                              title="Opções"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M7 10l5 5 5-5z" fill="currentColor"/></svg>
                            </button>
                            {openActionMenuId === msgId && createPortal(
                              // Desenhado no corpo da página, por cima de tudo: pode sair dos limites do chat
                              <div
                                className="chat-msg-action-dropdown chat-msg-action-dropdown-flutuante"
                                ref={actionMenuDropdownRef}
                                style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
                              >
                                <div className="chat-msg-action-item" onClick={() => { setSelectMode(true); setSelectedMsgIds(new Set([msgId])); setOpenActionMenuId(null); }}>
                                  <svg width="16" height="16" viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#4caf50"/></svg>
                                  <span>Selecionar</span>
                                </div>
                                {!isFileMessage && (
                                  <div className="chat-msg-action-item" onClick={() => { iniciarEdicao(m); setOpenActionMenuId(null); }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="rgb(235, 133, 0)"/></svg>
                                    <span>Editar</span>
                                  </div>
                                )}
                                <div className="chat-msg-action-item delete" onClick={() => { setConfirmDeleteMsgId(msgId); setOpenActionMenuId(null); }}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="#f44336"/></svg>
                                  <span>Apagar</span>
                                </div>
                              </div>,
                              document.body
                            )}
                          </div>
                        )}
                        {isEditing ? (
                          <div className="chat-message-edit-container">
                            <input
                              type="text"
                              className="chat-message-edit-input"
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                              onKeyDown={handleEditKeyDown}
                              autoFocus
                              disabled={savingEdit}
                            />
                            <button 
                              className="chat-message-edit-save"
                              onClick={salvarEdicao}
                              disabled={savingEdit || !editingText.trim()}
                              aria-label="Salvar edição"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" style={{ display: 'block', fill: 'white' }}>
                                <polygon points="6,3 20,12 6,21" />
                              </svg>
                            </button>
                            <button 
                              className="chat-message-edit-cancel"
                              onClick={cancelarEdicao}
                              disabled={savingEdit}
                            >
                              ×
                            </button>
                          </div>
                        ) : isImageFile ? (
                          /* Imagem */
                          <div className="chat-message-image-wrapper">
                            {(() => {
                              let imgSrc;
                              if (temArquivoPessoa) {
                                // ChatPessoas: usar objectURL cacheado
                                imgSrc = pessoaFileUrls[msgId];
                                if (!imgSrc) {
                                  // Ainda não carregou, disparar fetch
                                  if (msgId && !(msgId in pessoaFileUrls)) fetchPessoaFile(msgId);
                                  return <div style={{ color: '#999', fontSize: 12, padding: 8 }}>Carregando imagem...</div>;
                                }
                              } else {
                                // ChatGrupo: arquivo inline base64 ou byte array
                                imgSrc = typeof arquivo === 'string'
                                  ? `data:${tipoArquivo || 'image/jpeg'};base64,${arquivo}`
                                  : URL.createObjectURL(new Blob([new Uint8Array(arquivo)], { type: tipoArquivo }));
                              }
                              return (
                                <img 
                                  src={imgSrc}
                                  alt="imagem" 
                                  className="chat-message-image-only"
                                  onClick={() => {
                                    setLightboxImages([{ url: imgSrc }]);
                                    setLightboxIndex(0);
                                    setLightboxOpen(true);
                                  }}
                                  style={{ cursor: 'pointer' }}
                                />
                              );
                            })()}
                            <div className="chat-message-time-outside">{formatDateTime(m.DataHora || m.dataHora)}</div>
                          </div>
                        ) : (isPdfFile || isDocFile) && temArquivo ? (
                          /* PDF/DOC */
                          <div className="chat-message-doc-wrapper">
                            <div 
                              className="chat-message-file-doc"
                              onClick={() => {
                                if (temArquivoPessoa) {
                                  // Usar objectURL cacheado para download
                                  const fileUrl = pessoaFileUrls[msgId];
                                  if (fileUrl) {
                                    const a = document.createElement('a');
                                    a.href = fileUrl;
                                    a.download = texto || 'arquivo';
                                    a.click();
                                  } else {
                                    // Fallback: buscar e baixar
                                    (async () => {
                                      try {
                                        const token = getAuthToken();
                                        const bases = [BASE_URL.replace(/\/api\/?$/, '')];
                                        for (const base of bases) {
                                          try {
                                            const res = await fetch(`${base}/api/ChatPessoas/${msgId}/arquivo`, {
                                              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                                            });
                                            if (res.ok) {
                                              const blob = await res.blob();
                                              const url = URL.createObjectURL(blob);
                                              const a = document.createElement('a');
                                              a.href = url;
                                              a.download = texto || 'arquivo';
                                              a.click();
                                              URL.revokeObjectURL(url);
                                              return;
                                            }
                                          } catch (e) { continue; }
                                        }
                                      } catch (e) { console.error('Erro ao baixar arquivo', e); }
                                    })();
                                  }
                                } else {
                                  const blob = typeof arquivo === 'string' 
                                    ? new Blob([Uint8Array.from(atob(arquivo), c => c.charCodeAt(0))], { type: tipoArquivo }) 
                                    : new Blob([new Uint8Array(arquivo)], { type: tipoArquivo });
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = texto || 'arquivo';
                                  a.click();
                                  URL.revokeObjectURL(url);
                                }
                              }}
                            >
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" fill="#888"/>
                              </svg>
                              <span className="chat-file-doc-name">{texto}</span>
                            </div>
                            <div className="chat-message-time-outside">{formatDateTime(m.DataHora || m.dataHora)}</div>
                          </div>
                        ) : (
                          (() => {
                            const isUniqueEmoji = isSingleEmoji(texto);
                            return (
                              <div className={`chat-message-bubble ${isMe ? 'mine' : 'other'} ${isUniqueEmoji ? 'emoji-only' : ''}`}>
                                {texto && texto !== '[Arquivo]' && texto !== '[Foto]' && (
                                  <span className={isUniqueEmoji ? 'emoji-large' : ''}>{texto}</span>
                                )}
                                <div className="chat-message-time">{formatDateTime(m.DataHora || m.dataHora)}</div>
                              </div>
                            );
                          })()
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
              )}

              {/* Preview de arquivos pendentes */}
              {!showGroupInfo && !showMediaGallery && arquivosPendentes.length > 0 && (
                <div className="chat-pending-files">
                  {arquivosPendentes.slice(0, 4).map((arq, idx) => (
                    <div key={arq.id} className="chat-pending-file">
                      {arq.isImage ? (
                        <img src={arq.preview} alt={arq.name} className="chat-pending-thumb" />
                      ) : (
                        <div className="chat-pending-doc">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" fill="#888"/>
                          </svg>
                        </div>
                      )}
                      <button 
                        className="chat-pending-remove"
                        onClick={() => removerArquivoPendente(arq.id)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {arquivosPendentes.length > 4 && (
                    <div 
                      className="chat-pending-more"
                      onClick={() => setShowAllFilesPopup(true)}
                    >
                      +{arquivosPendentes.length - 4}
                    </div>
                  )}
                </div>
              )}

              {/* Input de mensagem */}
              {!showGroupInfo && !showMediaGallery && (
              <div className="chat-widget-input-area" style={{ position: 'relative' }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  multiple
                  onChange={handleFileChange}
                />
                <input
                  type="file"
                  ref={cameraInputRef}
                  style={{ display: 'none' }}
                  accept="image/*"
                  capture="user"
                  onChange={handleFileChange}
                />
                <button 
                  className="chat-widget-upload-btn"
                  onClick={() => setShowUploadPopup(true)}
                  disabled={uploading}
                >
                  +
                </button>
                
                {/* Emoji button */}
                <button 
                  ref={emojiButtonRef}
                  className="chat-widget-emoji-btn"
                  onClick={toggleEmoji}
                  disabled={enviando || uploading}
                  title="Emoji"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22">
                    <circle cx="12" cy="12" r="10" fill="rgb(235, 133, 0)" />
                    <circle cx="9" cy="10" r="0.9" fill="#333" />
                    <circle cx="15" cy="10" r="0.9" fill="#333" />
                    <path d="M12 17.5c2.33 0 4.31-1.46 5.11-3.5h-10.22c.8 2.04 2.78 3.5 5.11 3.5z" fill="#333" />
                  </svg>
                </button>
                
                {/* Emoji dropdown */}
                {emojiOpen && (
                  <div className="chat-emoji-dropdown" ref={emojiRef} role="menu">
                    <div className="chat-emoji-tabs">
                      {CATEGORIAS_EMOJI.map(cat => (
                        <button
                          key={cat.chave}
                          className={`chat-emoji-tab ${emojiCategory === cat.chave ? 'active' : ''}`}
                          onClick={() => setEmojiCategory(cat.chave)}
                          title={cat.titulo}
                          style={cat.bandeiras ? ESTILO_BANDEIRA : undefined}
                        >
                          {cat.emojis[0]}
                        </button>
                      ))}
                    </div>
                    <div className="chat-emoji-grid" role="menu">
                      {(CATEGORIAS_EMOJI.find(cat => cat.chave === emojiCategory) || CATEGORIAS_EMOJI[0]).emojis.map((emoji, i) => (
                        <button
                          key={i}
                          className="chat-emoji-item"
                          onClick={() => onEmojiSelect(emoji)}
                          style={emojiCategory === 'flags' ? ESTILO_BANDEIRA : undefined}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                <input
                  type="text"
                  className="chat-widget-input"
                  placeholder="Digite sua mensagem..."
                  value={novaMensagem}
                  onChange={(e) => setNovaMensagem(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={enviando || uploading}
                />
                <button 
                  className="chat-widget-send-btn"
                  onClick={() => enviarMensagem()}
                  disabled={enviando || uploading || (!novaMensagem.trim() && arquivosPendentes.length === 0)}
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="26" height="26">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="white"/>
                  </svg>
                </button>
              </div>
              )}
            </>
          )}
        </div>
      )}
      
      {/* Popup de escolha de upload */}
      {showUploadPopup && (
        <div className="chat-upload-popup-overlay" onClick={() => setShowUploadPopup(false)}>
          <div className="chat-upload-popup" onClick={(e) => e.stopPropagation()}>
            <div className="chat-upload-options">
              <div 
                className="chat-upload-option"
                onClick={() => {
                  abrirCamera();
                }}
              >
                <div className="chat-upload-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 15.2c1.8 0 3.2-1.4 3.2-3.2s-1.4-3.2-3.2-3.2-3.2 1.4-3.2 3.2 1.4 3.2 3.2 3.2zm0-5.5c1.3 0 2.3 1 2.3 2.3s-1 2.3-2.3 2.3-2.3-1-2.3-2.3 1-2.3 2.3-2.3z" fill="rgb(235, 133, 0)"/>
                    <path d="M20 4h-3.2L15 2H9L7.2 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h4.5l1.7-2h3.6l1.7 2H20v12z" fill="rgb(235, 133, 0)"/>
                  </svg>
                </div>
                <span className="chat-upload-label">Abrir câmera</span>
              </div>
              
              <div 
                className="chat-upload-option"
                onClick={() => {
                  setShowUploadPopup(false);
                  fileInputRef.current?.click();
                }}
              >
                <div className="chat-upload-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" fill="rgb(235, 133, 0)"/>
                  </svg>
                </div>
                <span className="chat-upload-label">Escolher imagem/arquivo</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal da câmera */}
      {showCamera && (
        <div className="chat-camera-overlay">
          <div className="chat-camera-container">
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline
              className="chat-camera-video"
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <div className="chat-camera-controls">
              <button 
                className="chat-camera-btn chat-camera-cancel"
                onClick={fecharCamera}
              >
                Cancelar
              </button>
              <button 
                className="chat-camera-btn chat-camera-capture"
                onClick={tirarFoto}
              >
                📷 Tirar Foto
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Popup de todos os arquivos pendentes */}
      {showAllFilesPopup && (
        <div className="chat-upload-popup-overlay" onClick={() => setShowAllFilesPopup(false)}>
          <div className="chat-all-files-popup" onClick={(e) => e.stopPropagation()}>
            <div className="chat-all-files-header">
              <span>Arquivos selecionados ({arquivosPendentes.length})</span>
              <button onClick={() => setShowAllFilesPopup(false)}>×</button>
            </div>
            <div className="chat-all-files-list">
              {arquivosPendentes.map((arq) => (
                <div key={arq.id} className="chat-all-files-item">
                  {arq.isImage ? (
                    <img src={arq.preview} alt={arq.name} className="chat-all-files-thumb" />
                  ) : (
                    <div className="chat-all-files-doc-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" fill="#888"/>
                      </svg>
                    </div>
                  )}
                  <span className="chat-all-files-name">{arq.name}</span>
                  <button 
                    className="chat-all-files-remove"
                    onClick={() => removerArquivoPendente(arq.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox para expandir imagens */}
      {lightboxOpen && (
        <Lightbox
          images={lightboxImages}
          index={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onNext={() => setLightboxIndex((lightboxIndex + 1) % lightboxImages.length)}
          onPrev={() => setLightboxIndex((lightboxIndex - 1 + lightboxImages.length) % lightboxImages.length)}
        />
      )}

      {/* Popup de confirmação para desfazer conexão */}
      {confirmDisconnect && (
        <div className="chat-upload-popup-overlay" onClick={() => setConfirmDisconnect(null)}>
          <div className="chat-confirm-popup" onClick={(e) => e.stopPropagation()}>
            <p className="chat-confirm-text">Tem certeza que deseja desfazer a conexão com <strong>{confirmDisconnect.nome}</strong>?</p>
            <div className="chat-confirm-buttons">
              <button className="chat-confirm-yes" onClick={() => handleDesfazerConexao(confirmDisconnect.id)} disabled={conexaoLoading}>
                {conexaoLoading ? '...' : 'Sim'}
              </button>
              <button className="chat-confirm-no" onClick={() => setConfirmDisconnect(null)}>
                Não
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup de confirmação para deletar mensagem */}
      {confirmDeleteMsgId && (
        <div className="chat-upload-popup-overlay" onClick={() => setConfirmDeleteMsgId(null)}>
          <div className="chat-confirm-popup" onClick={(e) => e.stopPropagation()}>
            <p className="chat-confirm-text">Tem certeza que deseja apagar esta mensagem?</p>
            <div className="chat-confirm-buttons">
              <button className="chat-confirm-yes" onClick={async () => { await deletarMensagem(confirmDeleteMsgId); setConfirmDeleteMsgId(null); }}>
                Sim
              </button>
              <button className="chat-confirm-no" onClick={() => setConfirmDeleteMsgId(null)}>
                Não
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup de confirmação para deletar mensagens selecionadas */}
      {confirmDeleteSelected && (
        <div className="chat-upload-popup-overlay" onClick={() => setConfirmDeleteSelected(false)}>
          <div className="chat-confirm-popup" onClick={(e) => e.stopPropagation()}>
            <p className="chat-confirm-text">Tem certeza que deseja apagar {selectedMsgIds.size} mensagen{selectedMsgIds.size !== 1 ? 's' : ''}?</p>
            <div className="chat-confirm-buttons">
              <button className="chat-confirm-yes" onClick={async () => { await deletarMensagensSelecionadas(); setConfirmDeleteSelected(false); }}>
                Sim
              </button>
              <button className="chat-confirm-no" onClick={() => setConfirmDeleteSelected(false)}>
                Não
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup de confirmação para carregar mensagens antigas */}
      {confirmLoadOlder && (
        <div className="chat-upload-popup-overlay" onClick={() => setConfirmLoadOlder(false)}>
          <div className="chat-confirm-popup" onClick={(e) => e.stopPropagation()}>
            <p className="chat-confirm-text">Deseja carregar todas as mensagens anteriores a 3 meses?</p>
            <div className="chat-confirm-buttons">
              <button className="chat-confirm-yes" onClick={() => {
                setShowAllMessages(true);
                setMensagens(allMensagens);
                setConfirmLoadOlder(false);
              }}>
                Sim
              </button>
              <button className="chat-confirm-no" onClick={() => setConfirmLoadOlder(false)}>
                Não
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup de confirmação para remover membro do grupo */}
      {confirmRemoveMember && (
        <div className="chat-upload-popup-overlay" onClick={() => setConfirmRemoveMember(null)}>
          <div className="chat-confirm-popup" onClick={(e) => e.stopPropagation()}>
            <p className="chat-confirm-text">Tem certeza que deseja remover <strong>{confirmRemoveMember.nome}</strong> do grupo?</p>
            <div className="chat-confirm-buttons">
              <button className="chat-confirm-yes" onClick={() => removerMembroDoGrupo(confirmRemoveMember)} disabled={removingMember}>
                {removingMember ? '...' : 'Sim'}
              </button>
              <button className="chat-confirm-no" onClick={() => setConfirmRemoveMember(null)}>
                Não
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup de confirmação para remover membros em lote */}
      {confirmBulkRemoveMembers && (
        <div className="chat-upload-popup-overlay" onClick={() => setConfirmBulkRemoveMembers(false)}>
          <div className="chat-confirm-popup" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <p className="chat-confirm-text">Deseja remover {selectedMemberIds.size} membro{selectedMemberIds.size !== 1 ? 's' : ''} do grupo?</p>
            <div className="chat-bulk-remove-list">
              {groupMembers.filter(m => selectedMemberIds.has(parseInt(m.id, 10))).map(m => (
                <div key={m.id} className="chat-bulk-remove-item">
                  <div className="chat-group-info-member-avatar" style={{ width: 28, height: 28, fontSize: 12 }}>
                    {userPhotos[m.id] ? (
                      <img src={userPhotos[m.id]} alt={m.nome} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                    ) : (
                      <span>{getInitial(m.nome)}</span>
                    )}
                  </div>
                  <span style={{ color: 'white', fontSize: 13 }}>{m.nome}</span>
                </div>
              ))}
            </div>
            <div className="chat-confirm-buttons">
              <button className="chat-confirm-yes" onClick={removerMembrosSelecionados} disabled={removingBulkMembers}>
                {removingBulkMembers ? '...' : 'Sim'}
              </button>
              <button className="chat-confirm-no" onClick={() => setConfirmBulkRemoveMembers(false)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup Gerenciar Admin */}
      {showAdminPopup && (
        <div className="chat-upload-popup-overlay" onClick={() => setShowAdminPopup(false)}>
          <div className="chat-admin-popup" onClick={(e) => e.stopPropagation()}>
            <div className="chat-admin-popup-header">
              <span>Gerenciar Administradores</span>
              <button className="chat-admin-popup-close" onClick={() => setShowAdminPopup(false)}>×</button>
            </div>
            <div className="chat-admin-popup-list">
              {/* Admins group */}
              <div className="chat-admin-section">
                <div className="chat-admin-section-title">Administradores</div>
                {groupMembers.filter(m => m.isAdmin && !m.isOwner).length === 0 && (
                  <div className="chat-group-info-empty">Nenhum administrador</div>
                )}
                {groupMembers.filter(m => m.isAdmin && !m.isOwner).map((member) => {
                  const mid = member.membershipId;
                  if (!mid) return null;
                  const isSelected = adminSelectedMembershipIds.has(mid);
                  return (
                    <div key={mid} className="chat-admin-popup-item">
                      <div className={`chat-select-checkbox admin-checkbox ${isSelected ? 'checked' : ''}`} onClick={() => toggleAdminSelection(mid)} style={{ marginRight: 8 }}>
                        {isSelected && <svg width="14" height="14" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white"/></svg>}
                      </div>
                      <div className="chat-group-info-member-avatar" style={{ width: 32, height: 32, fontSize: 13 }}>
                        {userPhotos[member.id] ? (
                          <img src={userPhotos[member.id]} alt={member.nome} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <span>{getInitial(member.nome)}</span>
                        )}
                      </div>
                      <div className="chat-admin-popup-item-info">
                        <div className="chat-admin-popup-item-name">{member.nome}</div>
                        {member.login && <div className="chat-admin-popup-item-login">{member.login}</div>}
                      </div>
                    </div>
                  );
                })}
                {/* Admins footer - remove selected */}
                {groupMembers.filter(m => m.isAdmin && !m.isOwner).length > 0 && (
                  <div className="chat-admin-section-footer">
                    <button className="chat-admin-remove-selected" onClick={batchRemoveAdmins} disabled={adminSelectedMembershipIds.size === 0 || adminSaving}>
                      Remover ({adminSelectedMembershipIds.size})
                    </button>
                  </div>
                )}
              </div>

              <div className="chat-admin-divider" />

              {/* Non-admins group */}
              <div className="chat-admin-section">
                <div className="chat-admin-section-title">Membros</div>
                {groupMembers.filter(m => !m.isAdmin && !m.isOwner).length === 0 && (
                  <div className="chat-group-info-empty">Nenhum membro</div>
                )}
                {groupMembers.filter(m => !m.isAdmin && !m.isOwner).map((member) => {
                  const mid = member.membershipId;
                  if (!mid) return null;
                  const isSelected = promoteSelectedMembershipIds.has(mid);
                  return (
                    <div key={mid} className="chat-admin-popup-item">
                      <div className={`chat-select-checkbox promote-checkbox ${isSelected ? 'checked' : ''}`} onClick={() => togglePromoteSelection(mid)} style={{ marginRight: 8 }}>
                        {isSelected && <svg width="14" height="14" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white"/></svg>}
                      </div>
                      <div className="chat-group-info-member-avatar" style={{ width: 32, height: 32, fontSize: 13 }}>
                        {userPhotos[member.id] ? (
                          <img src={userPhotos[member.id]} alt={member.nome} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <span>{getInitial(member.nome)}</span>
                        )}
                      </div>
                      <div className="chat-admin-popup-item-info">
                        <div className="chat-admin-popup-item-name">{member.nome}</div>
                        {member.login && <div className="chat-admin-popup-item-login">{member.login}</div>}
                      </div>
                    </div>
                  );
                })}
                {/* Non-admins footer - promote selected */}
                {groupMembers.filter(m => !m.isAdmin && !m.isOwner).length > 0 && (
                  <div className="chat-admin-section-footer">
                    <button className="chat-admin-promote-selected" onClick={batchMakeAdmins} disabled={promoteSelectedMembershipIds.size === 0 || adminSaving}>
                      Tornar Admin ({promoteSelectedMembershipIds.size})
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Popup Adicionar Membros */}
      {showAddMembersPopup && (
        <div className="chat-upload-popup-overlay" onClick={() => setShowAddMembersPopup(false)}>
          <div className="chat-add-members-popup" onClick={(e) => e.stopPropagation()}>
            <div className="chat-admin-popup-header">
              <span>Adicionar Membros</span>
              <button className="chat-admin-popup-close" onClick={() => setShowAddMembersPopup(false)}>×</button>
            </div>
            <div className="chat-add-members-search">
              <input
                type="text"
                placeholder="Pesquisar usuários..."
                value={addMemberSearch}
                onChange={(e) => setAddMemberSearch(e.target.value)}
                className="chat-add-members-search-input"
                autoFocus
              />
            </div>
            <div className="chat-add-members-list">
              {addMemberLoading && <div className="chat-group-info-empty">Buscando...</div>}
              {!addMemberLoading && addMemberSearch.trim() && addMemberResults.length === 0 && (
                <div className="chat-group-info-empty">Nenhum usuário encontrado</div>
              )}
              {!addMemberLoading && !addMemberSearch.trim() && (
                <div className="chat-group-info-empty">Digite para pesquisar</div>
              )}
              {addMemberResults.map((user) => {
                const userId = parseInt(user.id, 10);
                const isSelected = addMemberSelected.has(userId);
                return (
                  <div key={userId} className={`chat-add-members-item ${isSelected ? 'selected' : ''}`} onClick={() => {
                    setAddMemberSelected(prev => {
                      const next = new Set(prev);
                      if (next.has(userId)) next.delete(userId);
                      else next.add(userId);
                      return next;
                    });
                  }}>
                    <div className={`chat-select-checkbox ${isSelected ? 'checked' : ''}`} style={{ marginRight: 8 }}>
                      {isSelected && <svg width="14" height="14" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white"/></svg>}
                    </div>
                    <div className="chat-group-info-member-avatar" style={{ width: 32, height: 32, fontSize: 13 }}>
                      {userPhotos[user.id] ? (
                        <img src={userPhotos[user.id]} alt={user.nome} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        <span>{getInitial(user.nome)}</span>
                      )}
                    </div>
                    <div className="chat-admin-popup-item-info">
                      <div className="chat-admin-popup-item-name">{user.nome}</div>
                      {user.login && <div className="chat-admin-popup-item-login">{user.login}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            {addMemberSelected.size > 0 && (
              <div className="chat-add-members-footer">
                <button className="chat-add-members-confirm-btn" onClick={adicionarMembrosAoGrupo} disabled={addingMembers}>
                  {addingMembers ? '...' : `Adicionar (${addMemberSelected.size})`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tooltip fixo do remetente (fora do overflow da área de mensagens) */}
      {senderTooltip && (
        <div
          className="chat-sender-tooltip-fixed"
          style={{
            position: 'fixed',
            top: senderTooltip.top,
            left: senderTooltip.left,
            transform: 'translateY(-100%)',
            zIndex: 999999999,
          }}
        >
          <div className="chat-sender-tooltip-name" style={{ color: senderTooltip.color }}>{senderTooltip.name}</div>
          {senderTooltip.login && <div className="chat-sender-tooltip-login">{senderTooltip.login}</div>}
        </div>
      )}
    </>
  );
}
